"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { ArrowRight } from "lucide-react";
import SplitText from "@/features/home/components/landing-v2/split-text";
import RotatingText from "@/features/home/components/landing-v2/rotating-text";
import { countRotatingTextElements } from "@/features/home/components/landing-v2/rotating-text-elements";
import BuildGalleryShader from "@/features/home/components/landing-v2/build-gallery-shader";
import DotField from "@/features/home/components/landing-v2/dot-field";
import ScrollReveal from "@/features/home/components/landing-v2/scroll-reveal";
import "@/features/home/components/landing-v2/landing-v2.css";

gsap.registerPlugin(ScrollTrigger, useGSAP);

const A = "/_enter_home/landing-v2";
const HERO_ASSET_DIR = `${A}/hero/optimized`;
const HERO_PORTAL_VIDEO_MANIFEST = `${A}/hero/portal-videos/manifest.json`;
const PORTAL_PATH = "M0 42.5C0 19.0279 19.0279 0 42.5 0C65.9721 0 85 19.0279 85 42.5V98.773C85 99.4507 84.4507 100 83.773 100H1.227C0.54935 100 0 99.4507 0 98.773V42.5Z";
const PORTAL_OUTLINE_PATH = "M1.227 100C0.54935 100 0 99.4507 0 98.773V42.5C0 19.0279 19.0279 0 42.5 0C65.9721 0 85 19.0279 85 42.5V98.773C85 99.4507 84.4507 100 83.773 100H1.227";
const HEADING_FROM = { opacity: 0, y: 40 };
const HEADING_TO = { opacity: 1, y: 0 };
const CJK_SCRIPT_PATTERN = /[\p{Script=Han}\p{Script=Hiragana}\p{Script=Katakana}\p{Script=Hangul}]/u;

function shouldUseSmartWrap(text) {
  return /\s/u.test(text) && !CJK_SCRIPT_PATTERN.test(text);
}

function AnimatedHeading({
  text,
  tag = "h2",
  manual = false,
  play = true,
  className = "",
  smartWrap = shouldUseSmartWrap(text),
}) {
  return (
    <SplitText
      key={`${smartWrap ? "word" : "character"}:${text}`}
      tag={tag}
      className={className}
      text={text}
      delay={25}
      duration={0.75}
      ease="power3.out"
      splitType="chars"
      from={HEADING_FROM}
      to={HEADING_TO}
      threshold={0.1}
      rootMargin="-80px"
      textAlign={null}
      display="block"
      smartWrap={smartWrap}
      manual={manual}
      play={play}
    />
  );
}

function BusinessCtaMotionLabel({ label }) {
  const characters = Array.from(label);

  return (
    <span className="business-card__motion-label">
      <span className="sr-only">{label}</span>
      {characters.map((character, index) => {
        const visibleCharacter = character === " " ? "\u00a0" : character;

        return (
          <span
            key={`${character}-${index}`}
            aria-hidden="true"
            className="business-card__motion-character"
            style={{ "--business-motion-character-index": index }}
          >
            <span className="business-card__motion-layer business-card__motion-layer--outgoing">
              {visibleCharacter}
            </span>
            <span className="business-card__motion-layer business-card__motion-layer--incoming">
              {visibleCharacter}
            </span>
          </span>
        );
      })}
    </span>
  );
}

function useRisingSection({
  sectionRef,
  contentSelector,
  triggerSelector = "h2",
  headingReveal,
  y = 64,
}) {
  useGSAP(
    (context, contextSafe) => {
      const section = sectionRef.current;
      const content = section?.querySelector(contentSelector);
      const trigger = section?.querySelector(triggerSelector);
      if (!section || !content || !trigger) return undefined;

      const revealHeading = contextSafe(() => headingReveal(true));
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (prefersReducedMotion) {
        revealHeading();
        gsap.set(content, {
          autoAlpha: 1,
          y: 0,
          clearProps: "transform,opacity,visibility,willChange",
        });
        return undefined;
      }

      gsap.set(content, {
        autoAlpha: 0,
        y,
        willChange: "transform, opacity",
      });

      const revealSection = contextSafe(() => {
        revealHeading();
        gsap.to(content, {
          autoAlpha: 1,
          y: 0,
          duration: 0.85,
          ease: "power3.out",
          clearProps: "transform,opacity,visibility,willChange",
        });
      });

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          revealSection();
        },
        {
          root: null,
          rootMargin: "0px 0px -18% 0px",
          threshold: 0,
        },
      );

      observer.observe(trigger);

      return () => observer.disconnect();
    },
    { scope: sectionRef },
  );
}

const heroMedia = [
  ["hero-right", "hero-right.png"],
  ["hero-left", "hero-left.png"],
  ["hero-art", "hero-art.png"],
  ["hero-portrait", "hero-portrait.png?v=30810116"],
  ["hero-mini", "hero-mini.png"],
  ["hero-website", "hero-website.png"],
  ["hero-bottom", "hero-bottom.png"],
];

const heroFlybyMedia = [
  { file: "Website.png", width: 210, height: 117, exitX: -820, exitY: -470, rotation: -8 },
  { file: "Website-1.png", width: 184, height: 102, exitX: 820, exitY: -450, rotation: 7 },
  { file: "Website-2.png", width: 188, height: 105, exitX: -880, exitY: 450, rotation: 9 },
  { file: "Website-3.png", width: 186, height: 105, exitX: 870, exitY: 430, rotation: -7 },
  { file: "Website-4.png", width: 182, height: 102, exitX: -220, exitY: -670, rotation: -5 },
  { file: "Website-5.png", width: 108, height: 160, exitX: 250, exitY: 670, rotation: 6 },
];

const heroMotion = [
  { rotation: 4, rotationX: -5, rotationY: -8, parallaxX: 24, parallaxY: 15, tilt: 2.8 },
  { rotation: -4, rotationX: 5, rotationY: 9, parallaxX: 29, parallaxY: 18, tilt: 3.2 },
  { rotation: -3, rotationX: -4, rotationY: 7, parallaxX: 17, parallaxY: 11, tilt: 2.1 },
  { rotation: 3, rotationX: 4, rotationY: -8, parallaxX: 25, parallaxY: 16, tilt: 3 },
  { rotation: -4, rotationX: 5, rotationY: 8, parallaxX: 21, parallaxY: 14, tilt: 2.6 },
  { rotation: 3, rotationX: -4, rotationY: -7, parallaxX: 14, parallaxY: 9, tilt: 1.8 },
  { rotation: 3, rotationX: 4, rotationY: -8, parallaxX: 19, parallaxY: 12, tilt: 2.3 },
];

const heroPhoneScrollDirections = [
  { x: 1, y: 1 },
  { x: -1, y: 1 },
  { x: -1, y: -1 },
  { x: -0.7, y: 1 },
  null,
  { x: 1, y: -1 },
  null,
];

const heroReleaseOrder = [2, 5, 0, 4, 1, 6, 3];
const heroPortalLaunchPoints = [
  { x: -0.2, y: -0.08 },
  { x: 0.2, y: -0.06 },
  { x: -0.27, y: 0.16 },
  { x: 0.25, y: 0.17 },
  { x: -0.11, y: 0.3 },
  { x: 0.12, y: 0.29 },
  { x: 0, y: 0.08 },
];
const HERO_FLYBY_STAGGER = 0.12;
const HERO_RELEASE_STAGGER = 0.12;
const HERO_PORTAL_FRONT_Z = 24;
const HERO_PORTAL_FRONT_DEPTH_STEP = 10;
const HERO_PORTAL_START_SCALE = 0.012;
const HERO_PORTAL_PASS_DURATION = 0.42;
const HERO_REVEAL_DURATION = 0.3;
const HERO_TRAVEL_DURATION = 1.28;
const HERO_SETTLE_DELAY = 0.95;
const HERO_FLIGHT_DURATION = HERO_PORTAL_PASS_DURATION + HERO_TRAVEL_DURATION;
const HERO_PORTAL_LEAF_PERSPECTIVE = 284;
const HERO_INTRO_PLAYBACK_RATE = 2.25;
const HERO_PORTAL_HOVER_DURATION = 0.8;
const HERO_PORTAL_HOVER_FADE_AT = 0.7;
const HERO_PORTAL_EDUCATION_DELAY = 0.45;
const HERO_PORTAL_EDUCATION_HOLD_DURATION = 0.9;
const HERO_PORTAL_EDUCATION_OPEN_HOLD_DURATION = (
  HERO_PORTAL_HOVER_DURATION + HERO_PORTAL_EDUCATION_HOLD_DURATION
);
const HERO_SCROLL_EXIT_MARGIN = 64;
const HERO_SCROLL_SCRUB = 0.45;
const HERO_SCROLL_PREFLIGHT_Z = 160;
const HERO_SCROLL_HOLD_MIN = 120;
const HERO_SCROLL_HOLD_MAX = 180;
const HERO_SCROLL_HOLD_RATIO = 0.16;
const ANYTHING_ROTATING_COLORS = ["#3370ff", "#d559ff", "#ff8400", "#a3d7ff"];
const ANYTHING_ROTATING_INITIAL = { y: "100%", opacity: 0 };
const ANYTHING_ROTATING_ANIMATE = { y: 0, opacity: 1 };
const ANYTHING_ROTATING_EXIT = { y: "-120%", opacity: 0 };
const ANYTHING_ROTATING_TRANSITION = { duration: 0.28, ease: [0.16, 1, 0.3, 1] };
const ANYTHING_WORD_BEAT = 0.68;
const ANYTHING_ORBIT_ENTRY_DURATION = 2.2;
const ANYTHING_SCROLL_SCRUB = 0.45;
function getHeroScrollHoldDistance() {
  return Math.round(Math.min(
    HERO_SCROLL_HOLD_MAX,
    Math.max(HERO_SCROLL_HOLD_MIN, window.innerHeight * HERO_SCROLL_HOLD_RATIO),
  ));
}

function getRandomPortalVideoIndex(videoCount, previousIndex) {
  if (videoCount <= 1) return 0;
  if (previousIndex < 0 || previousIndex >= videoCount) {
    return Math.floor(Math.random() * videoCount);
  }

  const candidate = Math.floor(Math.random() * (videoCount - 1));
  return candidate >= previousIndex ? candidate + 1 : candidate;
}

function getAxisExitDistance(unit, center, mediaSize, canvasSize) {
  if (Math.abs(unit) < 0.001) return Number.POSITIVE_INFINITY;

  const boundary = unit > 0
    ? canvasSize + mediaSize / 2 + HERO_SCROLL_EXIT_MARGIN
    : -mediaSize / 2 - HERO_SCROLL_EXIT_MARGIN;

  return (boundary - center) / unit;
}

function getHeroScrollExitOffset(
  { rect, centerX, centerY },
  heroRect,
  launchX,
  launchY,
  directionOverride,
) {
  let directionX = directionOverride?.x ?? centerX - launchX;
  let directionY = directionOverride?.y ?? centerY - launchY;

  if (Math.hypot(directionX, directionY) < 1) {
    directionX = centerX - heroRect.width / 2;
    directionY = centerY - heroRect.height / 2;
  }

  if (Math.hypot(directionX, directionY) < 1) directionY = -1;

  const directionLength = Math.hypot(directionX, directionY);
  const unitX = directionX / directionLength;
  const unitY = directionY / directionLength;
  const horizontalDistance = getAxisExitDistance(unitX, centerX, rect.width, heroRect.width);
  const verticalDistance = getAxisExitDistance(unitY, centerY, rect.height, heroRect.height);
  const exitDistance = Math.min(horizontalDistance, verticalDistance);

  return {
    xPercent: (unitX * exitDistance / rect.width) * 100,
    yPercent: (unitY * exitDistance / rect.height) * 100,
  };
}

function countAnythingTitleCharacters(value) {
  return Array.from(value).length;
}

function renderAnythingTitleCharacters(value, offset = 0) {
  return Array.from(value).map((character, index) => (
    <span
      className="anything-scene__title-char"
      aria-hidden="true"
      key={`${character}-${offset + index}`}
    >
      {character === " " ? "\u00a0" : character}
    </span>
  ));
}

function HeroPortal({ onClick }) {
  const tHeaderActions = useTranslations("header.actions");

  return (
    <button
      type="button"
      className="hero-portal"
      onClick={onClick}
      aria-label={tHeaderActions("signIn")}
      data-landing-button-id="hero.portal"
      data-landing-button-screen="hero"
      data-landing-action="login"
      data-landing-creation-type="app"
      data-landing-triggers-login="true"
    >
      <svg className="hero-portal__clip-definitions" aria-hidden="true">
        <defs>
          <clipPath id="hero-portal-content-clip" clipPathUnits="objectBoundingBox">
            <path
              d={PORTAL_PATH}
              transform="scale(0.0117647058823529 0.01)"
            />
          </clipPath>
        </defs>
      </svg>
      <span className="hero-portal__opening">
        <span className="hero-portal__fallback" />
        <span className="hero-portal__gradient" aria-hidden="true" />
        <video
          className="hero-portal__video"
          aria-hidden="true"
          muted
          playsInline
          loop
          preload="metadata"
        />
      </span>
      <span className="hero-portal__mask">
        <span className="hero-portal__leaf">
          <svg viewBox="0 0 85 100" preserveAspectRatio="none" aria-hidden="true">
            <path
              d={PORTAL_PATH}
              fill="#fafafa"
            />
          </svg>
        </span>
      </span>
      <svg className="hero-portal__outline" viewBox="0 0 85 100" preserveAspectRatio="none" fill="none">
        <path
          className="hero-portal__path"
          d={PORTAL_OUTLINE_PATH}
          stroke="currentColor"
          strokeDasharray="0 1"
        />
      </svg>
    </button>
  );
}

function Hero({ onIntroComplete, prompt, announcement, onPortalClick }) {
  const heroRef = useRef(null);
  const [revealHeading, setRevealHeading] = useState(false);
  const t = useTranslations("landingV2.hero");
  const heading = t("heading");
  const smartWrapHeading = shouldUseSmartWrap(heading);

  useGSAP(
    (context, contextSafe) => {
      const hero = heroRef.current;
      if (!hero) return undefined;

      const portal = hero.querySelector(".hero-portal");
      const portalPath = hero.querySelector(".hero-portal__path");
      const portalOutline = hero.querySelector(".hero-portal__outline");
      const portalOpening = hero.querySelector(".hero-portal__opening");
      const portalGradient = hero.querySelector(".hero-portal__gradient");
      const portalVideo = hero.querySelector(".hero-portal__video");
      const portalMask = hero.querySelector(".hero-portal__mask");
      const portalLeaf = hero.querySelector(".hero-portal__leaf");
      const portalPathLength = portalPath.getTotalLength();
      const portalStrokeProgress = { value: 0 };
      const renderPortalStrokeProgress = () => {
        const visibleLength = portalPathLength * portalStrokeProgress.value;
        portalPath.setAttribute("stroke-dasharray", `${visibleLength} ${portalPathLength}`);
      };
      const flybyMedia = [...hero.querySelectorAll(".hero-flyby")];
      const finalMedia = [...hero.querySelectorAll(".hero-media")];
      const promptPanel = hero.querySelector(".hero__prompt-group");
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const hoverMediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
      const completeIntro = contextSafe(() => onIntroComplete?.(true));
      const portalVideoManifestController = new AbortController();
      let portalVideoSources = [];
      let previousPortalVideoIndex = -1;

      if (prefersReducedMotion) {
        hero.classList.add("hero__stage--intro-complete");
        setRevealHeading(true);
        portalStrokeProgress.value = 1;
        renderPortalStrokeProgress();
        gsap.set(portalOutline, { autoAlpha: 1 });
        gsap.set([portalOpening, portalMask], { autoAlpha: 0 });
        gsap.set(flybyMedia, { autoAlpha: 0 });
        gsap.set([...finalMedia, promptPanel], { autoAlpha: 1, clearProps: "transform" });
        let portalOpen = false;
        const handleReducedMotionPortalClick = (event) => {
          if (hoverMediaQuery.matches) return;
          event.preventDefault();
          event.stopPropagation();
          portalOpen = !portalOpen;
          portal.setAttribute("aria-pressed", String(portalOpen));
          gsap.set(portalOutline, { autoAlpha: portalOpen ? 0 : 1 });
          gsap.set([portalOpening, portalMask], { autoAlpha: portalOpen ? 1 : 0 });
          gsap.set(portalLeaf, {
            autoAlpha: portalOpen ? 0 : 1,
            rotationY: portalOpen ? 90 : 0,
          });
        };
        portal.addEventListener("click", handleReducedMotionPortalClick);
        completeIntro();
        return () => {
          hero.classList.remove("hero__stage--intro-complete");
          portal.removeEventListener("click", handleReducedMotionPortalClick);
          portal.removeAttribute("aria-pressed");
        };
      }

      const heroRect = hero.getBoundingClientRect();
      const portalRect = portal.getBoundingClientRect();
      const portalCenter = {
        x: portalRect.left - heroRect.left + portalRect.width / 2,
        y: portalRect.top - heroRect.top + portalRect.height / 2,
      };
      const heroComputedStyle = getComputedStyle(hero);
      const [perspectiveOriginX, perspectiveOriginY] = heroComputedStyle.perspectiveOrigin
        .split(" ")
        .map(Number.parseFloat);
      const perspectiveDepth = Number.parseFloat(heroComputedStyle.perspective) || 1100;
      const centerBeforeProjection = (targetX, targetY, z) => {
        const depthRatio = (perspectiveDepth - z) / perspectiveDepth;
        return {
          x: perspectiveOriginX + (targetX - perspectiveOriginX) * depthRatio,
          y: perspectiveOriginY + (targetY - perspectiveOriginY) * depthRatio,
        };
      };
      const finalMeasurements = finalMedia.map((media, index) => {
        const rect = media.getBoundingClientRect();
        const finalScale = Number.parseFloat(gsap.getProperty(media, "scaleX")) || 1;
        return {
          media,
          index,
          rect,
          finalScale,
          centerX: rect.left - heroRect.left + rect.width / 2,
          centerY: rect.top - heroRect.top + rect.height / 2,
        };
      });
      const flybyMeasurements = flybyMedia.map((media, index) => {
        const rect = media.getBoundingClientRect();
        return { media, index, rect };
      });

      gsap.set(promptPanel, { autoAlpha: 0, y: 24, willChange: "transform, opacity" });
      gsap.set(finalMedia, { autoAlpha: 0, zIndex: 4, willChange: "transform, opacity" });
      gsap.set(flybyMedia, { autoAlpha: 0, willChange: "transform, opacity" });
      portalStrokeProgress.value = 0;
      renderPortalStrokeProgress();
      gsap.set(portalOutline, { autoAlpha: 1 });
      gsap.set([portalOpening, portalMask], { autoAlpha: 0 });
      gsap.set(portalGradient, { autoAlpha: 0, animationPlayState: "paused" });
      gsap.set(portalLeaf, {
        autoAlpha: 1,
        rotationY: 0,
        transformOrigin: "0% 50%",
        transformPerspective: HERO_PORTAL_LEAF_PERSPECTIVE,
      });
      let parallaxReady = false;
      let interactionRect = heroRect;
      const parallaxControllers = finalMeasurements.map(({ media, index }) => {
        const config = heroMotion[index];
        return {
          config,
          x: gsap.quickTo(media, "x", { duration: 0.85, ease: "power3.out", overwrite: "auto" }),
          y: gsap.quickTo(media, "y", { duration: 0.85, ease: "power3.out", overwrite: "auto" }),
          rotationX: gsap.quickTo(media, "rotationX", { duration: 1, ease: "power3.out", overwrite: "auto" }),
          rotationY: gsap.quickTo(media, "rotationY", { duration: 1, ease: "power3.out", overwrite: "auto" }),
        };
      });

      const refreshInteractionRect = () => {
        interactionRect = hero.getBoundingClientRect();
      };

      const setParallax = (normalizedX, normalizedY) => {
        parallaxControllers.forEach((controller) => {
          const { config } = controller;
          controller.x(normalizedX * config.parallaxX);
          controller.y(normalizedY * config.parallaxY);
          controller.rotationX(-normalizedY * config.tilt * 0.72);
          controller.rotationY(normalizedX * config.tilt);
        });
      };

      const handlePointerMove = (event) => {
        if (!parallaxReady) return;
        const normalizedX = Math.max(-1, Math.min(1, ((event.clientX - interactionRect.left) / interactionRect.width) * 2 - 1));
        const normalizedY = Math.max(-1, Math.min(1, ((event.clientY - interactionRect.top) / interactionRect.height) * 2 - 1));
        setParallax(normalizedX, normalizedY);
      };

      const resetParallax = () => {
        if (parallaxReady) setParallax(0, 0);
      };

      let scrollExitMedia;
      const createScrollExit = contextSafe(() => {
        const heroSection = hero.closest(".hero");
        if (!heroSection) return;

        scrollExitMedia?.revert();
        scrollExitMedia = gsap.matchMedia();
        scrollExitMedia.add(
          {
            isPhone: "(max-width: 640px)",
            isLargerViewport: "(min-width: 641px)",
          },
          ({ conditions }) => {
            const currentHeroRect = hero.getBoundingClientRect();
            const currentPortalRect = portal.getBoundingClientRect();
            const currentPortalCenter = {
              x: currentPortalRect.left - currentHeroRect.left + currentPortalRect.width / 2,
              y: currentPortalRect.top - currentHeroRect.top + currentPortalRect.height / 2,
            };
            const scrollMediaMeasurements = finalMedia
              .map((media, index) => {
                const rect = media.getBoundingClientRect();
                return {
                  media,
                  index,
                  rect,
                  centerX: rect.left - currentHeroRect.left + rect.width / 2,
                  centerY: rect.top - currentHeroRect.top + rect.height / 2,
                };
              })
              .filter(({ rect }) => rect.width > 0 && rect.height > 0);

            if (scrollMediaMeasurements.length === 0) return undefined;

            const scrollExitOffsets = scrollMediaMeasurements.map((measurement) => {
              const launchPoint = heroPortalLaunchPoints[(measurement.index + 3) % heroPortalLaunchPoints.length];
              const launchX = currentPortalCenter.x + launchPoint.x * currentPortalRect.width;
              const launchY = currentPortalCenter.y + launchPoint.y * currentPortalRect.height;
              const directionOverride = conditions.isPhone
                ? heroPhoneScrollDirections[measurement.index]
                : null;

              return getHeroScrollExitOffset(
                measurement,
                currentHeroRect,
                launchX,
                launchY,
                directionOverride,
              );
            });
            const scrollMedia = scrollMediaMeasurements.map(({ media }) => media);
            const heroSectionDocumentTop = heroSection.getBoundingClientRect().top + window.scrollY;
            const preflightTween = gsap.fromTo(
              scrollMedia,
              { z: 0 },
              {
                z: HERO_SCROLL_PREFLIGHT_Z,
                ease: "none",
                scrollTrigger: {
                  id: "landing-v2-hero-media-preflight",
                  trigger: heroSection,
                  start: () => `top top+=${heroSectionDocumentTop}`,
                  end: () => `+=${getHeroScrollHoldDistance()}`,
                  scrub: HERO_SCROLL_SCRUB,
                  pin: heroSection,
                  pinSpacing: true,
                  anticipatePin: 1,
                  refreshPriority: -10,
                },
              },
            );
            const preflightTrigger = preflightTween.scrollTrigger;

            if (!preflightTrigger) return undefined;

            gsap.fromTo(
              scrollMedia,
              { xPercent: 0, yPercent: 0 },
              {
                xPercent: (index) => scrollExitOffsets[index].xPercent,
                yPercent: (index) => scrollExitOffsets[index].yPercent,
                ease: "none",
                scrollTrigger: {
                  id: "landing-v2-hero-media-exit",
                  trigger: heroSection,
                  start: () => preflightTrigger.end,
                  end: () => preflightTrigger.end + heroSection.offsetHeight,
                  scrub: HERO_SCROLL_SCRUB,
                  refreshPriority: -9,
                },
              },
            );

            return undefined;
          },
        );
      });

      let portalInteractive = false;
      let portalContentActive = false;
      let portalEducationActive = false;
      let portalEducationCancelled = false;
      let portalTouchActive = false;
      const isPortalEngaged = () => (
        portal.matches(":focus-visible")
        || (hoverMediaQuery.matches && portal.matches(":hover"))
      );
      const isPortalOpenRequested = () => (
        portalEducationActive || portalTouchActive || isPortalEngaged()
      );
      const hidePortalVideo = () => {
        portalVideo?.removeAttribute("data-playing");
      };
      const stopPortalVideo = () => {
        if (!portalVideo) return;
        portalVideo.pause();
        hidePortalVideo();
      };
      const playRandomPortalVideo = () => {
        if (!portalVideo || portalVideoSources.length === 0) return;

        const nextIndex = getRandomPortalVideoIndex(
          portalVideoSources.length,
          previousPortalVideoIndex,
        );
        const nextSource = portalVideoSources[nextIndex];
        previousPortalVideoIndex = nextIndex;
        hidePortalVideo();

        if (portalVideo.getAttribute("src") === nextSource) {
          portalVideo.currentTime = 0;
        } else {
          portalVideo.src = nextSource;
          portalVideo.load();
        }

        void portalVideo.play().catch(hidePortalVideo);
      };
      const handlePortalVideoPlaying = () => {
        portalVideo?.setAttribute("data-playing", "true");
      };
      const settlePortal = contextSafe(() => {
        portalContentActive = false;
        stopPortalVideo();
        gsap.set([portalOpening, portalMask], { autoAlpha: 0 });
      });
      const portalInteractionTimeline = gsap.timeline({
        paused: true,
        defaults: { ease: "power2.inOut" },
        onReverseComplete: settlePortal,
      });

      portalInteractionTimeline
        .to(portalOutline, { autoAlpha: 0, duration: HERO_PORTAL_HOVER_DURATION }, 0)
        .to(
          portalLeaf,
          {
            rotationY: 90,
            transformPerspective: HERO_PORTAL_LEAF_PERSPECTIVE,
            duration: HERO_PORTAL_HOVER_DURATION,
            force3D: true,
          },
          0,
        )
        .to(portalLeaf, { autoAlpha: 0, duration: 0.1, ease: "none" }, HERO_PORTAL_HOVER_FADE_AT);

      const loadPortalVideoSources = async ({ playWhenOpen = false } = {}) => {
        try {
          const response = await fetch(HERO_PORTAL_VIDEO_MANIFEST, {
            cache: "no-store",
            signal: portalVideoManifestController.signal,
          });
          if (!response.ok) return;

          const manifest = await response.json();
          if (!Array.isArray(manifest?.videos)) return;

          portalVideoSources = manifest.videos.filter(
            (source) => typeof source === "string" && source.length > 0,
          );
          if (
            playWhenOpen
            && portalContentActive
            && portalInteractive
            && isPortalOpenRequested()
          ) {
            playRandomPortalVideo();
          }
        } catch (error) {
          if (error?.name !== "AbortError") hidePortalVideo();
        }
      };

      const openPortal = contextSafe(() => {
        if (!portalInteractive) return;
        if (!portalContentActive) {
          portalContentActive = true;
          const hasCachedVideoSources = portalVideoSources.length > 0;
          if (hasCachedVideoSources) playRandomPortalVideo();
          void loadPortalVideoSources({ playWhenOpen: !hasCachedVideoSources });
        }
        gsap.set([portalOpening, portalMask], { autoAlpha: 1 });
        portalInteractionTimeline.play();
      });
      const closePortal = () => {
        if (portalInteractive && !isPortalOpenRequested()) {
          portalInteractionTimeline.reverse();
        }
      };
      const educationClock = { progress: 0 };
      const portalEducationTimeline = gsap.timeline({ paused: true });
      const cancelPortalEducation = () => {
        portalEducationCancelled = true;
        portalEducationActive = false;
        portalEducationTimeline.kill();
      };
      const openInteractivePortal = contextSafe(() => {
        cancelPortalEducation();
        openPortal();
      });
      const closeInteractivePortal = () => {
        cancelPortalEducation();
        portalTouchActive = false;
        portal.removeAttribute("aria-pressed");
        closePortal();
      };
      const handlePortalTouchClick = contextSafe((event) => {
        if (hoverMediaQuery.matches) return;
        event.preventDefault();
        event.stopPropagation();
        if (!portalInteractive) return;
        cancelPortalEducation();
        portalTouchActive = !portalTouchActive;
        portal.setAttribute("aria-pressed", String(portalTouchActive));
        if (portalTouchActive) {
          openPortal();
        } else {
          portalInteractionTimeline.reverse();
        }
      });

      portalEducationTimeline
        .to(educationClock, {
          progress: 1,
          duration: HERO_PORTAL_EDUCATION_DELAY,
          ease: "none",
        })
        .call(() => {
          if (portalEducationCancelled) return;
          portalEducationActive = true;
          openPortal();
        })
        .to(educationClock, {
          progress: 2,
          duration: HERO_PORTAL_EDUCATION_OPEN_HOLD_DURATION,
          ease: "none",
        })
        .call(() => {
          if (portalEducationCancelled) return;
          portalEducationActive = false;
          closePortal();
        });

      portalVideo?.addEventListener("playing", handlePortalVideoPlaying);
      portalVideo?.addEventListener("error", hidePortalVideo);
      void loadPortalVideoSources();

      const timeline = gsap.timeline({
        paused: true,
        defaults: { ease: "power3.out" },
        onComplete: () => {
          hero.classList.add("hero__stage--intro-complete");
          gsap.set(finalMedia, { clearProps: "transform,opacity,visibility,z-index,will-change" });
          gsap.set(flybyMedia, { clearProps: "transform,z-index,will-change" });
          gsap.set(promptPanel, { clearProps: "transform,opacity,visibility,will-change" });
          refreshInteractionRect();
          parallaxReady = true;
          portalInteractive = true;
          createScrollExit();
          if (isPortalEngaged()) {
            openInteractivePortal();
          } else {
            portalEducationTimeline.play(0);
          }
          completeIntro();
        },
      });

      timeline
        .addLabel("draw", 0)
        .fromTo(
          portalStrokeProgress,
          { value: 0 },
          {
            value: 1,
            duration: 1.25,
            ease: "power2.inOut",
            onUpdate: renderPortalStrokeProgress,
          },
          "draw+=0.15",
        )
        .addLabel("open", ">+=0.18")
        .set(portalGradient, { autoAlpha: 1, animationPlayState: "running" }, "open")
        .set([portalOpening, portalMask], { autoAlpha: 1 }, "open")
        .to(
          portalOutline,
          { autoAlpha: 0, duration: 1.1, ease: "power2.inOut" },
          "open",
        )
        .fromTo(
          portalLeaf,
          {
            autoAlpha: 1,
            rotationY: 0,
            transformPerspective: HERO_PORTAL_LEAF_PERSPECTIVE,
          },
          {
            rotationY: 90,
            transformPerspective: HERO_PORTAL_LEAF_PERSPECTIVE,
            duration: 1.1,
            ease: "power2.inOut",
            force3D: true,
          },
          "open",
        )
        .to(portalLeaf, { autoAlpha: 0, duration: 0.1, ease: "none" }, "open+=0.98")
        .addLabel("flyby", "open+=0.92");

      flybyMeasurements.forEach(({ media, index, rect }) => {
        if (rect.width === 0 || rect.height === 0) return;
        const config = heroFlybyMedia[index];
        const launchPoint = heroPortalLaunchPoints[index % heroPortalLaunchPoints.length];
        const launchX = portalCenter.x + launchPoint.x * portalRect.width;
        const launchY = portalCenter.y + launchPoint.y * portalRect.height;
        const baseCenterX = rect.left - heroRect.left + rect.width / 2;
        const baseCenterY = rect.top - heroRect.top + rect.height / 2;
        const startZ = HERO_PORTAL_FRONT_Z + index * HERO_PORTAL_FRONT_DEPTH_STEP;
        const startCenter = centerBeforeProjection(launchX, launchY, startZ);
        const exitCenter = centerBeforeProjection(
          portalCenter.x + config.exitX,
          portalCenter.y + config.exitY,
          720,
        );
        const offset = index * HERO_FLYBY_STAGGER;
        const flightAt = `flyby+=${offset}`;
        const fadeAt = `flyby+=${offset + HERO_FLIGHT_DURATION - 0.2}`;

        timeline
          .fromTo(
            media,
            {
              x: startCenter.x - baseCenterX,
              y: startCenter.y - baseCenterY,
              z: startZ,
              scale: HERO_PORTAL_START_SCALE,
              rotation: config.rotation * 0.25,
              rotationX: -4,
              rotationY: 5,
              zIndex: 12 + index,
              autoAlpha: 0,
              transformOrigin: "50% 50%",
            },
            {
              x: exitCenter.x - baseCenterX,
              y: exitCenter.y - baseCenterY,
              z: 720,
              scale: 1.15,
              rotation: config.rotation,
              rotationX: 0,
              rotationY: 0,
              duration: HERO_FLIGHT_DURATION,
              ease: "none",
              force3D: true,
              immediateRender: false,
            },
            flightAt,
          )
          .to(media, { autoAlpha: 1, duration: HERO_REVEAL_DURATION, ease: "none" }, flightAt)
          .to(media, { autoAlpha: 0, duration: 0.2, ease: "none" }, fadeAt);
      });

      timeline.addLabel("release", `flyby+=${HERO_SETTLE_DELAY}`);

      finalMeasurements.forEach(({ media, index, rect, finalScale, centerX, centerY }) => {
        if (rect.width === 0 || rect.height === 0) return;
        const config = heroMotion[index];
        const orderIndex = heroReleaseOrder.indexOf(index);
        const launchPoint = heroPortalLaunchPoints[(index + 3) % heroPortalLaunchPoints.length];
        const launchX = portalCenter.x + launchPoint.x * portalRect.width;
        const launchY = portalCenter.y + launchPoint.y * portalRect.height;
        const offset = orderIndex * HERO_RELEASE_STAGGER;
        const flightAt = `release+=${offset}`;
        const startZ = HERO_PORTAL_FRONT_Z + orderIndex * HERO_PORTAL_FRONT_DEPTH_STEP;
        const startCenter = centerBeforeProjection(launchX, launchY, startZ);

        timeline
          .fromTo(
            media,
            {
              x: startCenter.x - centerX,
              y: startCenter.y - centerY,
              z: startZ,
              scale: Math.max(finalScale * HERO_PORTAL_START_SCALE, 0.01),
              rotation: config.rotation,
              rotationX: config.rotationX,
              rotationY: config.rotationY,
              zIndex: 5 + orderIndex,
              autoAlpha: 0,
              transformOrigin: "50% 50%",
            },
            {
              x: 0,
              y: 0,
              z: 0,
              scale: finalScale,
              rotation: 0,
              rotationX: 0,
              rotationY: 0,
              duration: HERO_FLIGHT_DURATION,
              ease: "sine.out",
              force3D: true,
              immediateRender: false,
            },
            flightAt,
          )
          .to(media, { autoAlpha: 1, duration: HERO_REVEAL_DURATION, ease: "none" }, flightAt);
      });

      timeline
        .addLabel(
          "close",
          `release+=${(heroReleaseOrder.length - 1) * HERO_RELEASE_STAGGER + HERO_FLIGHT_DURATION - 0.18}`,
        )
        .set(portalOutline, { autoAlpha: 1 }, "close")
        .set(portalMask, { autoAlpha: 1 }, "close")
        .set(
          portalLeaf,
          {
            autoAlpha: 0,
            rotationY: 90,
            transformPerspective: HERO_PORTAL_LEAF_PERSPECTIVE,
          },
          "close",
        )
        .to(portalLeaf, { autoAlpha: 1, duration: 0.08, ease: "none" }, "close")
        .to(
          portalLeaf,
          {
            rotationY: 0,
            transformPerspective: HERO_PORTAL_LEAF_PERSPECTIVE,
            duration: 1.1,
            ease: "power2.inOut",
            force3D: true,
          },
          "close",
        )
        .to(portalOpening, { autoAlpha: 0, duration: 0.12, ease: "none" }, "close+=0.98")
        .to(portalMask, { autoAlpha: 0, duration: 0.1, ease: "none" }, "close+=1.08")
        .set(portalGradient, { autoAlpha: 0, animationPlayState: "paused" }, "close+=1.1")
        .addLabel("reveal", "close+=1.05")
        .call(() => setRevealHeading(true), [], "reveal+=0.18")
        .fromTo(
          promptPanel,
          { autoAlpha: 0, y: 24 },
          { autoAlpha: 1, y: 0, duration: 0.75, ease: "power3.out" },
          "reveal+=0.82",
        );

      const observer = new IntersectionObserver(
        ([entry]) => {
          if (!entry.isIntersecting) return;
          timeline.timeScale(HERO_INTRO_PLAYBACK_RATE).play(0);
          observer.disconnect();
        },
        { threshold: 0.2 },
      );
      observer.observe(hero);

      portal.addEventListener("focus", openInteractivePortal);
      portal.addEventListener("blur", closeInteractivePortal);
      portal.addEventListener("click", handlePortalTouchClick);
      if (hoverMediaQuery.matches) {
        portal.addEventListener("pointerenter", openInteractivePortal);
        portal.addEventListener("pointerleave", closeInteractivePortal);
        hero.addEventListener("pointerenter", refreshInteractionRect);
        hero.addEventListener("pointermove", handlePointerMove);
        hero.addEventListener("pointerleave", resetParallax);
        window.addEventListener("resize", refreshInteractionRect);
        window.addEventListener("scroll", refreshInteractionRect, { passive: true });
      }

      return () => {
        hero.classList.remove("hero__stage--intro-complete");
        scrollExitMedia?.revert();
        observer.disconnect();
        portalEducationTimeline.kill();
        portalVideoManifestController.abort();
        stopPortalVideo();
        portalVideo?.removeEventListener("playing", handlePortalVideoPlaying);
        portalVideo?.removeEventListener("error", hidePortalVideo);
        portal.removeEventListener("pointerenter", openInteractivePortal);
        portal.removeEventListener("pointerleave", closeInteractivePortal);
        portal.removeEventListener("focus", openInteractivePortal);
        portal.removeEventListener("blur", closeInteractivePortal);
        portal.removeEventListener("click", handlePortalTouchClick);
        portal.removeAttribute("aria-pressed");
        hero.removeEventListener("pointerenter", refreshInteractionRect);
        hero.removeEventListener("pointermove", handlePointerMove);
        hero.removeEventListener("pointerleave", resetParallax);
        window.removeEventListener("resize", refreshInteractionRect);
        window.removeEventListener("scroll", refreshInteractionRect);
      };
    },
    { scope: heroRef },
  );

  return (
    <section className="hero" id="top" data-landing-screen="hero">
      <div ref={heroRef} className="hero__stage">
        {heroFlybyMedia.map(({ file, width, height }) => (
          <div className="hero-flyby" key={file} style={{ width, height }} aria-hidden="true">
            <img src={`${HERO_ASSET_DIR}/${file}`} alt="" draggable="false" decoding="async" />
          </div>
        ))}
        {heroMedia.map(([className, file]) => (
          <div className={`hero-media hero-media--${className}`} key={file} aria-hidden="true">
            <img src={`${HERO_ASSET_DIR}/${file}`} alt="" draggable="false" decoding="async" />
          </div>
        ))}
        <div className="hero__content">
          <HeroPortal onClick={onPortalClick} />
          {announcement ? (
            <div className="hero__announcement">
              {announcement}
            </div>
          ) : null}
          <AnimatedHeading
            className="hero__headline"
            tag="div"
            text={heading}
            smartWrap={smartWrapHeading}
            manual
            play={revealHeading}
          />
          <div className="hero__prompt-group">
            {prompt}
          </div>
        </div>
      </div>
    </section>
  );
}

const buildGalleryCategoryConfig = [
  {
    id: "apps",
    buildMode: "apps",
    items: [
      { key: "aiTools", image: "build-types/apps/ai-tools.png" },
      { key: "dashboards", image: "build-types/apps/dashboards.png" },
      { key: "internalTools", image: "build-types/apps/internal-tools.png" },
      { key: "marketplaces", image: "build-types/apps/marketplaces.png" },
      { key: "mobileApps", image: "build-types/apps/mobile-apps.png" },
      { key: "saasApps", image: "build-types/apps/saas-apps.png" },
    ],
  },
  {
    id: "websites",
    buildMode: "apps",
    items: [
      { key: "landingPages", image: "build-types/websites/landing-pages.png" },
      { key: "brandWebsites", image: "build-types/websites/brand-websites.png" },
      { key: "portfolios", image: "build-types/websites/portfolios.png?v=fa4256ab" },
      { key: "onlineStores", image: "build-types/websites/online-stores.png" },
      { key: "blogs", image: "build-types/websites/blogs.png" },
      { key: "productLaunchPages", image: "build-types/websites/product-launch-pages.png" },
    ],
  },
  {
    id: "agents",
    buildMode: "agents",
    items: [
      { key: "supportAgents", image: "build-types/agents/support-agents.png" },
      { key: "knowledgeAgents", image: "build-types/agents/knowledge-agents.png" },
      { key: "growthAgents", image: "build-types/agents/growth-agents.png" },
      { key: "analysisAgents", image: "build-types/agents/analysis-agents.png" },
      { key: "researchAgents", image: "build-types/agents/research-agents.png" },
      { key: "salesAgents", image: "build-types/agents/sales-agents.png" },
    ],
  },
];

const BUILD_GALLERY_GAP = 55;
const BUILD_GALLERY_SPEED = 38;

function BuildGallerySurface({ category, incomingCategory, ctaLabel, isTransitioning, onBuild }) {
  return (
    <div className="build-gallery__surface build-gallery__surface--current">
      <div className="build-gallery__track">
        {[0, 1, 2, 3].map((groupIndex) => {
          const duplicate = groupIndex > 0;
          return (
            <div
              className={`build-gallery__group${duplicate ? " build-gallery__group--duplicate" : ""}`}
              aria-hidden={duplicate || undefined}
              key={groupIndex}
            >
              {category.items.map((item, index) => {
                const incomingItem = incomingCategory?.items[index];
                return (
                  <button
                    type="button"
                    className="build-gallery-card"
                    data-refresh-index={index}
                    aria-label={`${ctaLabel}: ${item.title}`}
                    tabIndex={duplicate ? -1 : undefined}
                    disabled={isTransitioning}
                    onClick={onBuild}
                    data-landing-button-id={`build_gallery.card.${category.id}.${item.key}`}
                    data-landing-button-screen="build_gallery"
                    data-landing-action="login"
                    data-landing-content-key={`${category.id}.${item.key}`}
                    data-landing-creation-type={
                      category.buildMode === "agents" ? "agent" : "app"
                    }
                    data-landing-triggers-login="true"
                    key={item.key}
                  >
                    <div className="build-gallery-card__media">
                      <img
                        className="build-gallery-card__image"
                        src={`${A}/${item.image}`}
                        alt={duplicate ? "" : item.title}
                        draggable="false"
                        loading="lazy"
                        decoding="async"
                      />
                      {incomingItem && (
                        <BuildGalleryShader
                          currentSrc={`${A}/${item.image}`}
                          incomingSrc={`${A}/${incomingItem.image}`}
                          index={index}
                        />
                      )}
                      <span className="build-gallery-card__cta" aria-hidden="true">
                        <span>{ctaLabel}</span>
                        <ArrowRight size={20} strokeWidth={1.5} />
                      </span>
                    </div>
                    <div className="build-gallery-card__copy-stack">
                      <div className="build-gallery-card__copy build-gallery-card__copy--current">
                        <h3>{item.title}</h3>
                        <p>{item.copy}</p>
                      </div>
                      {incomingItem && (
                        <div className="build-gallery-card__copy build-gallery-card__copy--incoming" aria-hidden="true">
                          <h3>{incomingItem.title}</h3>
                          <p>{incomingItem.copy}</p>
                        </div>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BuildTypes({ onBuildAction }) {
  const t = useTranslations("landingV2.buildGallery");
  const sectionRef = useRef(null);
  const tabsRef = useRef(null);
  const transitionLockRef = useRef(false);
  const queuedCategoryIdRef = useRef(null);
  const [activeCategoryId, setActiveCategoryId] = useState("apps");
  const [renderedCategoryId, setRenderedCategoryId] = useState("apps");
  const [incomingCategoryId, setIncomingCategoryId] = useState(null);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const buildGalleryCategories = buildGalleryCategoryConfig.map(({ id, buildMode, items }) => ({
    id,
    buildMode,
    label: t(`categories.${id}.label`),
    items: items.map(({ key, image }) => ({
      key,
      image,
      title: t(`categories.${id}.items.${key}.title`),
      copy: t(`categories.${id}.items.${key}.copy`),
    })),
  }));
  const renderedCategory = buildGalleryCategories.find(({ id }) => id === renderedCategoryId) ?? buildGalleryCategories[0];
  const incomingCategory = buildGalleryCategories.find(({ id }) => id === incomingCategoryId) ?? null;

  useEffect(() => {
    const sources = new Set(buildGalleryCategoryConfig.flatMap(({ items }) => items.map(({ image }) => image)));
    const preloads = [...sources].map((source) => {
      const image = new Image();
      image.decoding = "async";
      image.src = `${A}/${source}`;
      return image;
    });
    return () => preloads.forEach((image) => {
      image.onload = null;
      image.onerror = null;
    });
  }, []);

  useEffect(() => {
    const tabs = tabsRef.current;
    if (!tabs) return undefined;

    let frame = 0;
    const updateIndicator = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        const activeTab = tabs.querySelector('[role="tab"][aria-selected="true"]');
        if (!activeTab) return;

        tabs.style.setProperty("--build-gallery-indicator-x", `${activeTab.offsetLeft}px`);
        tabs.style.setProperty("--build-gallery-indicator-scale", String(activeTab.offsetWidth));
        tabs.dataset.indicatorReady = "true";
      });
    };

    updateIndicator();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(updateIndicator);
    observer?.observe(tabs);
    [...tabs.querySelectorAll('[role="tab"]')].forEach((tab) => observer?.observe(tab));
    window.addEventListener("resize", updateIndicator);

    return () => {
      window.cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener("resize", updateIndicator);
    };
  }, [activeCategoryId]);

  const requestCategory = (id) => {
    if (id === activeCategoryId) return;
    setActiveCategoryId(id);

    if (transitionLockRef.current) {
      queuedCategoryIdRef.current = id;
      return;
    }

    transitionLockRef.current = true;
    queuedCategoryIdRef.current = null;
    setIncomingCategoryId(id);
    setIsTransitioning(true);
  };

  useGSAP(
    () => {
      const section = sectionRef.current;
      if (!section) return undefined;

      const viewport = section.querySelector(".build-gallery__viewport");
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (!viewport || prefersReducedMotion) {
        if (viewport) gsap.set(viewport, { autoAlpha: 1, y: 0 });
        return undefined;
      }

      gsap.fromTo(
        viewport,
        { autoAlpha: 0, y: 64 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.85,
          ease: "power3.out",
          clearProps: "transform,opacity,visibility",
          scrollTrigger: {
            id: "build-gallery-reveal",
            trigger: viewport,
            start: "top 72%",
            toggleActions: "play none none none",
            once: true,
          },
        },
      );
      return undefined;
    },
    { scope: sectionRef },
  );

  useGSAP(
    () => {
      const section = sectionRef.current;
      if (!section) return undefined;

      const track = section.querySelector(".build-gallery__track");
      const firstGroup = section.querySelector(".build-gallery__group");
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const isDesktop = window.matchMedia("(min-width: 901px)").matches;

      if (!track || !firstGroup || prefersReducedMotion || !isDesktop) return undefined;

      const loopDistance = firstGroup.scrollWidth + BUILD_GALLERY_GAP;
      const marquee = gsap.fromTo(
        track,
        { x: -180 },
        {
          x: -180 - loopDistance,
          duration: loopDistance / BUILD_GALLERY_SPEED,
          ease: "none",
          repeat: -1,
          paused: true,
        },
      );

      const sectionBounds = section.getBoundingClientRect();
      if (sectionBounds.bottom > 0 && sectionBounds.top < window.innerHeight) marquee.play();

      const playbackTrigger = ScrollTrigger.create({
        id: "build-gallery-playback",
        trigger: section,
        start: "top bottom",
        end: "bottom top",
        onEnter: () => marquee.play(),
        onEnterBack: () => marquee.play(),
        onLeave: () => marquee.pause(),
        onLeaveBack: () => marquee.pause(),
      });

      return () => {
        playbackTrigger.kill();
        marquee.kill();
      };
    },
    { scope: sectionRef },
  );

  useGSAP(
    (context, contextSafe) => {
      if (!incomingCategoryId) return undefined;

      let disposed = false;
      let commitFrame = 0;
      let settleFrame = 0;
      const commitCategory = contextSafe(() => {
        if (disposed) return;

        const committedCategoryId = incomingCategoryId;
        const queuedCategoryId = queuedCategoryIdRef.current;
        setRenderedCategoryId(committedCategoryId);

        if (queuedCategoryId && queuedCategoryId !== committedCategoryId) {
          queuedCategoryIdRef.current = null;
          setIncomingCategoryId(queuedCategoryId);
          setIsTransitioning(true);
          return;
        }

        queuedCategoryIdRef.current = null;
        transitionLockRef.current = false;
        setIncomingCategoryId(null);
        setIsTransitioning(false);
      });
      const section = sectionRef.current;
      const cards = [...(section?.querySelectorAll(".build-gallery-card") ?? [])];
      const shaderCanvases = [...(section?.querySelectorAll(".build-gallery-card__shader") ?? [])];
      const activeShaderCanvases = shaderCanvases.filter((canvas) => {
        const bounds = canvas.getBoundingClientRect();
        return bounds.right >= -160 && bounds.left <= window.innerWidth + 160;
      });
      const baseImages = cards.map((card) => card.querySelector(".build-gallery-card__image"));
      const currentCopies = cards.map((card) => card.querySelector(".build-gallery-card__copy--current"));
      const incomingCopies = cards.map((card) => card.querySelector(".build-gallery-card__copy--incoming"));
      const cannotAnimate = (
        !cards.length
        || !activeShaderCanvases.length
        || baseImages.some((image) => !image)
        || currentCopies.some((copy) => !copy)
        || incomingCopies.some((copy) => !copy)
      );
      if (cannotAnimate) {
        commitCategory();
        return undefined;
      }
      const prepareCategoryCommit = contextSafe(async () => {
        await Promise.all(baseImages.map(async (image) => {
          const cardIndex = Number(image.closest(".build-gallery-card")?.dataset.refreshIndex ?? 0);
          const nextSource = `${A}/${incomingCategory.items[cardIndex].image}`;
          image.src = nextSource;
          try {
            await image.decode();
          } catch {
            // The preload completed but decode may be unsupported; keep the final shader frame until paint.
          }
        }));
        if (disposed) return;

        commitFrame = window.requestAnimationFrame(() => {
          settleFrame = window.requestAnimationFrame(commitCategory);
        });
      });
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      if (prefersReducedMotion) {
        commitCategory();
        return undefined;
      }

      gsap.set(shaderCanvases, { "--shader-progress": -0.56 });
      gsap.set(incomingCopies, { autoAlpha: 0 });

      const refreshTimeline = gsap.timeline({
        paused: true,
        defaults: { ease: "none" },
        onComplete: prepareCategoryCommit,
      });

      refreshTimeline.addLabel("refresh", 0);

      for (let index = 0; index < incomingCategory.items.length; index += 1) {
        const indexedCanvases = shaderCanvases.filter((canvas) => Number(canvas.dataset.refreshIndex) === index);
        const startAt = index * 0.04;

        refreshTimeline.to(
          indexedCanvases,
          {
            "--shader-progress": 1.52,
            duration: 1.02,
            ease: "power1.inOut",
          },
          `refresh+=${startAt}`,
        );
      }

      refreshTimeline
        .to(currentCopies, { autoAlpha: 0, duration: 0.2, ease: "power1.out" }, "refresh+=0.4")
        .to(incomingCopies, { autoAlpha: 1, duration: 0.24, ease: "power2.out" }, "refresh+=0.46");

      let fallbackTimeout;
      const startWhenReady = () => {
        if (!activeShaderCanvases.every((canvas) => canvas.dataset.shaderReady === "true")) return;
        section.removeEventListener("build-gallery-shader-ready", startWhenReady);
        window.clearTimeout(fallbackTimeout);
        window.requestAnimationFrame(() => refreshTimeline.play(0));
      };

      section.addEventListener("build-gallery-shader-ready", startWhenReady);
      fallbackTimeout = window.setTimeout(() => {
        section.removeEventListener("build-gallery-shader-ready", startWhenReady);
        if (activeShaderCanvases.every((canvas) => canvas.dataset.shaderReady === "true")) {
          refreshTimeline.play(0);
        } else {
          commitCategory();
        }
      }, 1600);
      startWhenReady();

      return () => {
        disposed = true;
        section.removeEventListener("build-gallery-shader-ready", startWhenReady);
        window.clearTimeout(fallbackTimeout);
        window.cancelAnimationFrame(commitFrame);
        window.cancelAnimationFrame(settleFrame);
        refreshTimeline.kill();
      };
    },
    {
      scope: sectionRef,
      dependencies: [incomingCategoryId],
      revertOnUpdate: true,
    },
  );

  return (
    <section ref={sectionRef} className="build-types" id="products"
      data-landing-screen="build_gallery"
    >
      <div className="build-types__visual">
        <AnimatedHeading text={t("heading")} />
        <div ref={tabsRef} className="build-gallery__tabs" role="tablist" aria-label={t("ariaLabel")}>
          {buildGalleryCategories.map(({ id, label }) => (
            <button
              className={`build-gallery__tab${activeCategoryId === id ? " is-active" : ""}`}
              type="button"
              role="tab"
              id={`build-gallery-tab-${id}`}
              aria-controls="build-gallery-panel"
              aria-selected={activeCategoryId === id}
              onClick={() => requestCategory(id)}
              data-landing-button-id={`build_gallery.tab.${id}`}
              data-landing-button-screen="build_gallery"
              data-landing-action="toggle"
              data-landing-content-key={id}
              key={id}
            >
              {label}
            </button>
          ))}
          <span className="build-gallery__tab-indicator" aria-hidden="true" />
        </div>
        <div
          className="build-gallery__viewport"
          id="build-gallery-panel"
          role="tabpanel"
          aria-labelledby={`build-gallery-tab-${activeCategoryId}`}
          aria-busy={isTransitioning}
        >
          <BuildGallerySurface
            category={renderedCategory}
            incomingCategory={incomingCategory}
            ctaLabel={t("cta")}
            isTransitioning={isTransitioning}
            onBuild={() => onBuildAction?.(
              renderedCategory.buildMode,
              `build-gallery-${renderedCategory.id}`,
            )}
          />
        </div>
      </div>
    </section>
  );
}

const EASY_BUILD_STEPS = [
  { key: "voice", video: "voice.mp4" },
  { key: "plan", video: "plan.mp4" },
  { key: "shape", video: "shape.mp4" },
  { key: "launch", video: "launch.mp4" },
];
const EASY_BUILD_STAGE_LIFT = 120;
const EASY_BUILD_COPY_OFFSET_X = 20;
const EASY_BUILD_STEP_GAP = 91.5;
const EASY_BUILD_ACTIVE_DESCRIPTION_SPACE = 83;

function getEasyBuildMediaSlot(index, activeIndex) {
  if (index === activeIndex) return "active";
  if (index === activeIndex - 1) return "previous";
  if (index === activeIndex + 1) return "next";
  return index < activeIndex ? "before" : "after";
}

function EasyBuild({ onBuildAction }) {
  const sectionRef = useRef(null);
  const videoRefs = useRef([]);
  const scrollTriggerRef = useRef(null);
  const [activeStep, setActiveStep] = useState(0);
  const [isSectionActive, setIsSectionActive] = useState(false);
  const [hasSubtitleRevealed, setHasSubtitleRevealed] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const t = useTranslations("landingV2.easyBuild");
  const steps = EASY_BUILD_STEPS.map(({ key, video }) => ({
    key,
    video,
    title: t(key),
    description: t(`${key}Description`),
  }));

  const handleStepSelect = useCallback((event) => {
    const stepIndex = Number(event.currentTarget.dataset.stepIndex);
    if (
      !Number.isInteger(stepIndex)
      || stepIndex < 0
      || stepIndex >= EASY_BUILD_STEPS.length
    ) return;

    setActiveStep(stepIndex);

    const scrollTrigger = scrollTriggerRef.current;
    if (scrollTrigger) {
      const targetProgress = (stepIndex + 0.5) / EASY_BUILD_STEPS.length;
      const targetScroll = scrollTrigger.start
        + ((scrollTrigger.end - scrollTrigger.start) * targetProgress);
      window.scrollTo({ top: targetScroll, behavior: "auto" });
      return;
    }

    if (window.matchMedia("(min-width: 901px)").matches) return;

    videoRefs.current[stepIndex]?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "center",
    });
  }, []);

  useEffect(() => {
    const desktopQuery = window.matchMedia("(min-width: 901px)");
    const updateDesktopState = () => setIsDesktop(desktopQuery.matches);

    updateDesktopState();
    desktopQuery.addEventListener("change", updateDesktopState);
    return () => desktopQuery.removeEventListener("change", updateDesktopState);
  }, []);

  useGSAP(
    (context, contextSafe) => {
      const section = sectionRef.current;
      if (!section) return undefined;

      const visual = section.querySelector(".easy-build__visual");
      const media = gsap.matchMedia();
      const updateActiveStep = contextSafe((progress) => {
        const nextStep = Math.min(
          EASY_BUILD_STEPS.length - 1,
          Math.floor(progress * EASY_BUILD_STEPS.length),
        );
        setActiveStep((currentStep) => (currentStep === nextStep ? currentStep : nextStep));
      });
      const updateSectionActivity = contextSafe((isActive) => {
        setIsSectionActive(isActive);
        if (isActive) setHasSubtitleRevealed(true);
      });
      const sectionObserver = new IntersectionObserver(
        ([entry]) => updateSectionActivity(entry.isIntersecting),
        { threshold: 0.2 },
      );
      sectionObserver.observe(section);
      let desktopLayout = { scrollDistance: 0, stageTop: 0 };
      const updateDesktopLayout = () => {
        if (!visual) return { stageTop: 0 };

        const header = document.querySelector(".landing-v2-header");
        const headerLogo = header?.querySelector(".site-header__logo");
        const headerSignIn = header?.querySelector(".site-header__login-btn");
        const headerBottom = Math.max(0, header?.getBoundingClientRect().bottom ?? 0);
        const leftAnchor = Math.max(0, headerLogo?.getBoundingClientRect().left ?? 40);
        const signInRight = headerSignIn?.getBoundingClientRect().right ?? window.innerWidth;
        const mediaRightAnchor = window.innerWidth > 1440
          ? Math.max(0, window.innerWidth - signInRight)
          : 0;
        const availableHeight = Math.max(1, window.innerHeight - headerBottom);
        const widthScale = Math.min(1, Math.max(0.01, (window.innerWidth - leftAnchor) / 1400));
        const heightScale = Math.min(1, availableHeight / 911);
        const scale = Math.min(widthScale, heightScale);
        const is1440Tier = window.innerWidth >= 1360 && window.innerWidth <= 1440;
        const copyScale = is1440Tier ? Math.min(1, scale + 0.06) : scale;
        const copyLeftAnchor = leftAnchor + EASY_BUILD_COPY_OFFSET_X + (is1440Tier ? 10 : 0);
        const stageHeight = 911 * scale;
        const centeredStageTop = headerBottom + Math.max(0, (availableHeight - stageHeight) / 2);
        const stageTop = Math.max(headerBottom, centeredStageTop - EASY_BUILD_STAGE_LIFT);
        const scrollDistance = Math.round(window.innerHeight * 2.4);

        section.style.setProperty("--easy-build-scale", String(scale));
        section.style.setProperty("--easy-build-copy-scale", String(copyScale));
        section.style.setProperty("--easy-build-left-anchor", `${copyLeftAnchor}px`);
        section.style.setProperty("--easy-build-right-anchor", `${mediaRightAnchor}px`);
        section.style.setProperty("--easy-build-heading-top", `${84 * scale}px`);
        section.style.setProperty("--easy-build-steps-top", `${393 * scale}px`);
        section.style.setProperty("--easy-build-stage-height", `${stageHeight}px`);
        section.style.setProperty("--easy-build-stage-top", `${stageTop}px`);
        section.style.setProperty("--easy-build-scroll-distance", `${scrollDistance}px`);
        return { scrollDistance, stageTop };
      };
      const resetDesktopLayout = () => {
        section.style.removeProperty("--easy-build-scale");
        section.style.removeProperty("--easy-build-copy-scale");
        section.style.removeProperty("--easy-build-left-anchor");
        section.style.removeProperty("--easy-build-right-anchor");
        section.style.removeProperty("--easy-build-heading-top");
        section.style.removeProperty("--easy-build-steps-top");
        section.style.removeProperty("--easy-build-stage-height");
        section.style.removeProperty("--easy-build-stage-top");
        section.style.removeProperty("--easy-build-scroll-distance");
      };

      media.add(
        {
          desktop: "(min-width: 901px)",
          reduceMotion: "(prefers-reduced-motion: reduce)",
        },
        (context) => {
          const { desktop, reduceMotion } = context.conditions;
          if (!desktop) return undefined;

          desktopLayout = updateDesktopLayout();
          if (reduceMotion) {
            setActiveStep(0);
            setIsSectionActive(false);
            return resetDesktopLayout;
          }

          const scrollTrigger = ScrollTrigger.create({
            id: "easy-build-scroll",
            trigger: section,
            start: () => `top ${desktopLayout.stageTop}px`,
            end: () => `+=${desktopLayout.scrollDistance}`,
            invalidateOnRefresh: true,
            onRefreshInit: () => {
              desktopLayout = updateDesktopLayout();
            },
            onUpdate: ({ progress }) => updateActiveStep(progress),
          });
          scrollTriggerRef.current = scrollTrigger;

          return () => {
            if (scrollTriggerRef.current === scrollTrigger) {
              scrollTriggerRef.current = null;
            }
            scrollTrigger.kill();
            resetDesktopLayout();
          };
        },
      );

      return () => {
        sectionObserver.disconnect();
        media.revert();
      };
    },
    { scope: sectionRef },
  );

  useEffect(() => {
    const videos = videoRefs.current.filter(Boolean);
    const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    videos.forEach((video) => video.pause());

    if (reduceMotion) {
      return () => videos.forEach((video) => video.pause());
    }

    if (!isDesktop) {
      const mobileVideoObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;
            if (!entry.isIntersecting) {
              video.pause();
              return;
            }

            try {
              video.currentTime = 0;
            } catch {
              // Metadata is not guaranteed to be ready on the first intersection.
            }
            void video.play().catch(() => undefined);
          });
        },
        { threshold: 0.35 },
      );

      videos.forEach((video) => mobileVideoObserver.observe(video));

      return () => {
        mobileVideoObserver.disconnect();
        videos.forEach((video) => video.pause());
      };
    }

    const activeVideo = videos[activeStep];
    const playActiveVideo = () => {
      if (!activeVideo || !isSectionActive) return;
      try {
        activeVideo.currentTime = 0;
      } catch {
        // Metadata is not guaranteed to be ready on the first intersection.
      }
      void activeVideo.play().catch(() => undefined);
    };

    if (activeVideo && isSectionActive) {
      if (activeVideo.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
        playActiveVideo();
      } else {
        activeVideo.addEventListener("loadeddata", playActiveVideo, { once: true });
      }
    }

    return () => {
      activeVideo?.removeEventListener("loadeddata", playActiveVideo);
      videos.forEach((video) => video.pause());
    };
  }, [activeStep, isDesktop, isSectionActive]);

  return (
    <section ref={sectionRef} className="easy-build" id="solutions"
      data-landing-screen="easy_build"
    >
      <div className="easy-build__visual">
        <div className="easy-build__heading">
          <AnimatedHeading text={t("heading")} />
          <ScrollReveal
            active={hasSubtitleRevealed}
            baseOpacity={0}
            baseRotation={5}
            enableBlur={false}
            textClassName="easy-build__subtitle"
          >
            {t("subtitle")}
          </ScrollReveal>
        </div>
        <div className="easy-build__steps" aria-label={t("ariaLabel")}>
          {steps.map((step, index) => {
            const isActive = activeStep === index;
            const offset = (
              (index * EASY_BUILD_STEP_GAP)
              + (index > activeStep ? EASY_BUILD_ACTIVE_DESCRIPTION_SPACE : 0)
            );

            return (
              <article
                className={`easy-build__step${isActive ? " is-active" : ""}`}
                key={step.key}
                style={{ "--step-offset": `${offset}px` }}
                aria-current={isActive ? "step" : undefined}
              >
                <div className="easy-build__step-heading">
                  <h3>
                    <button
                      className="easy-build__step-trigger"
                      type="button"
                      data-step-index={index}
                      aria-pressed={isActive}
                      onClick={handleStepSelect}
                      data-landing-button-id={`easy_build.step.${step.key}`}
                      data-landing-button-screen="easy_build"
                      data-landing-action="toggle"
                      data-landing-content-key={step.key}
                    >
                      {step.title}
                    </button>
                  </h3>
                  <button
                    type="button"
                    className="easy-build__step-arrow"
                    aria-label={`${t("cta")}: ${step.title}`}
                    aria-hidden={!isActive}
                    tabIndex={isActive ? undefined : -1}
                    onClick={() => onBuildAction?.("apps", "easy-build")}
                    data-landing-button-id={`easy_build.build.${step.key}`}
                    data-landing-button-screen="easy_build"
                    data-landing-action="login"
                    data-landing-content-key={step.key}
                    data-landing-creation-type="app"
                    data-landing-triggers-login="true"
                  >
                    <span className="easy-build__step-arrow-mask" aria-hidden="true">
                      <img
                        className="easy-build__step-arrow-icon easy-build__step-arrow-icon--outgoing"
                        src={`${A}/easy-build/arrow-right.svg`}
                        alt=""
                      />
                      <img
                        className="easy-build__step-arrow-icon easy-build__step-arrow-icon--incoming"
                        src={`${A}/easy-build/arrow-right.svg`}
                        alt=""
                      />
                    </span>
                  </button>
                </div>
                <ScrollReveal
                  active={isDesktop ? isSectionActive && isActive : undefined}
                  baseOpacity={0}
                  baseRotation={5}
                  enableBlur={false}
                  rotationEnd="bottom center"
                  textClassName="easy-build__step-description"
                  wordAnimationEnd="bottom center"
                >
                  {step.description}
                </ScrollReveal>
              </article>
            );
          })}
        </div>
        <div className="easy-build__media" aria-hidden="true">
          {steps.map((step, index) => {
            const slot = getEasyBuildMediaSlot(index, activeStep);
            return (
              <video
                className={`easy-build__video is-${slot}`}
                key={step.key}
                ref={(element) => {
                  videoRefs.current[index] = element;
                }}
                src={`${A}/easy-build/${step.video}`}
                muted
                loop
                playsInline
                preload="metadata"
                tabIndex={-1}
              />
            );
          })}
        </div>
      </div>
    </section>
  );
}

const SCALE_ARCH_REVEAL_START = "top 42%";
const SCALE_ARCH_REVEAL_SCRUB = 0.6;
const SCALE_NODE_VERTICAL_OFFSET = 24;
const SCALE_PORTAL_OPEN_START = 0;

const scaleNodes = [
  {
    nameKey: "localization",
    icon: "scale-v2/globe.svg",
    positionClassName: "left-[359px] top-[515px] w-12",
    iconClassName: "h-[34.8254px] w-[34.8254px]",
    labelClassName: "text-[calc(17.485px-var(--landing-v2-locale-font-offset))] tracking-[-0.1748px]",
  },
  {
    nameKey: "aiModels",
    icon: "scale-v2/webhook.svg",
    positionClassName: "left-[471px] top-[515px] w-12",
    iconClassName: "h-[33.2432px] w-[34.8254px]",
    labelClassName: "text-[calc(17.763px-var(--landing-v2-locale-font-offset))] tracking-[-0.1776px]",
  },
  {
    nameKey: "agents",
    icon: "scale-v2/brain.svg",
    positionClassName: "left-[568px] top-[514px] w-[57px]",
    badgeClassName: "bg-black/[0.075]",
    iconClassName: "h-[34.8327px] w-[32.452px]",
    labelClassName: "text-[calc(16.803px-var(--landing-v2-locale-font-offset))] tracking-[-0.168px]",
  },
  {
    nameKey: "payments",
    icon: "scale-v2/image.svg",
    positionClassName: "left-[814px] top-[514px] w-[57px]",
    badgeClassName: "bg-black/[0.075]",
    iconClassName: "size-[31.6667px]",
    labelClassName: "text-[calc(16.803px-var(--landing-v2-locale-font-offset))] tracking-[-0.168px]",
  },
  {
    nameKey: "analytics",
    icon: "scale-v2/chart.svg",
    positionClassName: "left-[908px] top-[515px]",
    iconClassName: "size-[31.6588px]",
    labelClassName: "text-[calc(16.705px-var(--landing-v2-locale-font-offset))] tracking-[-0.167px]",
  },
  {
    nameKey: "cloud",
    icon: "scale-v2/cloud.svg",
    positionClassName: "left-[1031px] top-[515px]",
    iconClassName: "h-[25.3267px] w-[34.8239px]",
    labelClassName: "text-[calc(17.898px-var(--landing-v2-locale-font-offset))] tracking-[-0.179px]",
  },
];

const anythingCollage = Array.from(
  { length: 16 },
  (_, index) => `anything/${String(index + 1).padStart(2, "0")}.png`,
);

function EasyScale({ onBuildAction }) {
  const sectionRef = useRef(null);
  const rotatingTextRef = useRef(null);
  const [anythingWordIndex, setAnythingWordIndex] = useState(0);
  const t = useTranslations("landingV2.easyScale");
  const anythingRotatingWords = t.raw("anythingWords");
  const anythingTitlePrefix = t("anythingTitlePrefix");
  const anythingTitleSuffix = t("anythingTitleSuffix");
  const anythingTitlePrefixCharCount = countAnythingTitleCharacters(anythingTitlePrefix);
  const anythingStaticCharCount =
    anythingTitlePrefixCharCount + countAnythingTitleCharacters(anythingTitleSuffix);
  const anythingInitialRotatingCharCount = countRotatingTextElements(anythingRotatingWords[0]);

  useEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;

    const updateResponsiveStageScale = () => {
      const availableWidth = Math.max(0, section.clientWidth - 32);
      const desktopSceneScale = Math.min(1.25, Math.max(1, section.clientWidth / 1440));
      const scaleSceneScale = Math.min(1, availableWidth / 786);
      const anythingSceneScale = Math.min(1.56, (availableWidth / 1000) * 2);
      const anythingSceneRight = Math.round(-1440 * anythingSceneScale * 0.75);
      const anythingSceneHeight = Math.max(
        800,
        Math.min(1240, Math.round(section.clientWidth * 1.72)),
      );

      section.style.setProperty("--easy-scale-desktop-scale", `${desktopSceneScale}`);
      section.style.setProperty("--easy-scale-mobile-scale", `${scaleSceneScale}`);
      section.style.setProperty(
        "--easy-scale-mobile-height",
        `${Math.round(680 * scaleSceneScale)}px`,
      );
      section.style.setProperty(
        "--easy-scale-mobile-offset-y",
        `${Math.round(-128 * scaleSceneScale)}px`,
      );
      section.style.setProperty("--anything-mobile-scale", `${anythingSceneScale}`);
      section.style.setProperty(
        "--anything-mobile-right",
        `${anythingSceneRight}px`,
      );
      section.style.setProperty(
        "--anything-mobile-height",
        `${anythingSceneHeight}px`,
      );
    };

    updateResponsiveStageScale();

    const resizeObserver = new ResizeObserver(updateResponsiveStageScale);
    resizeObserver.observe(section);

    return () => resizeObserver.disconnect();
  }, []);

  useGSAP(
    () => {
      const section = sectionRef.current;
      if (!section) return undefined;

      const visual = section.querySelector(".scale-journey__visual");
      const graph = section.querySelector(".scale-graph");
      const portal = section.querySelector(".scale-portal");
      const portalLeaf = section.querySelector(".scale-portal__leaf");
      const copy = section.querySelector(".easy-scale__copy--desktop");
      const nodes = [...section.querySelectorAll(".scale-node")];
      const nodeContents = [...section.querySelectorAll(".scale-node__content")];
      const nodeIcons = [...section.querySelectorAll(".scale-node__badge")];
      const nodeLabels = [...section.querySelectorAll(".scale-node__label")];
      const anythingScene = section.querySelector(".anything-scene");
      const anythingStage = section.querySelector(".anything-scene__stage");
      const anythingTitle = section.querySelector(".anything-scene__title");
      const titleChars = [...section.querySelectorAll(".anything-scene__title-char")];
      const rotatingWord = section.querySelector(".anything-scene__rotating-word");
      const initialRotatingChars = [...section.querySelectorAll(".anything-scene__rotating-element")];
      const cards = [...section.querySelectorAll(".anything__image")];
      const baseCardImages = cards.map((card) => card.querySelector(".anything__image-base"));
      const rings = [
        section.querySelector(".scale-arc--small"),
        section.querySelector(".scale-arc--medium"),
        section.querySelector(".scale-arc--large"),
      ];
      const ringPlanes = [
        section.querySelector(".scale-arc-plane--small"),
        section.querySelector(".scale-arc-plane--medium"),
        section.querySelector(".scale-arc-plane--large"),
      ];
      const media = gsap.matchMedia();

      if (
        !visual
        || !graph
        || !portal
        || !portalLeaf
        || !copy
        || !anythingScene
        || !anythingStage
        || !anythingTitle
        || !rotatingWord
        || titleChars.length !== anythingStaticCharCount
        || initialRotatingChars.length !== anythingInitialRotatingCharCount
        || rings.some((ring) => !ring)
        || ringPlanes.some((plane) => !plane)
        || nodes.length !== 6
        || nodeContents.length !== 6
        || nodeIcons.length !== 6
        || nodeLabels.length !== 6
        || cards.length !== 16
        || baseCardImages.some((image) => !image)
      ) {
        return undefined;
      }

      const stageCenter = { x: 720, y: 455 };
      const cardMotion = cards.map((card) => {
        const centerX = card.offsetLeft + (card.offsetWidth / 2);
        const centerY = card.offsetTop + (card.offsetHeight / 2);
        const vx = centerX - stageCenter.x;
        const vy = centerY - stageCenter.y;

        return {
          card,
          angle: Math.atan2(vy, vx),
          finalX: vx,
          finalY: vy,
        };
      }).sort((a, b) => a.angle - b.angle);
      const orbitEaseOut = gsap.parseEase("power3.out");
      const orbitEaseIn = gsap.parseEase("power2.in");
      const clamp01 = gsap.utils.clamp(0, 1);
      const lerp = (from, to, amount) => from + ((to - from) * amount);

      const createCardOrbitRenderer = (orbitDriver) => {
        const orbitSetters = cardMotion.map((motion, order) => ({
          ...motion,
          order,
          slotAngle: -Math.PI + ((order / cardMotion.length) * Math.PI * 2),
          setX: gsap.quickSetter(motion.card, "x", "px"),
          setY: gsap.quickSetter(motion.card, "y", "px"),
          setZ: gsap.quickSetter(motion.card, "z", "px"),
          setScaleX: gsap.quickSetter(motion.card, "scaleX"),
          setScaleY: gsap.quickSetter(motion.card, "scaleY"),
          setRotation: gsap.quickSetter(motion.card, "rotation", "deg"),
          setOpacity: gsap.quickSetter(motion.card, "opacity"),
        }));

        return () => {
          const progress = clamp01(orbitDriver.progress);
          const entryEnd = 0.2;
          const exitStart = 0.78;
          const entryPhase = orbitEaseOut(clamp01(progress / entryEnd));
          const holdPhase = orbitEaseOut(
            clamp01((progress - entryEnd) / (exitStart - entryEnd)),
          );
          const exitPhase = orbitEaseIn(
            clamp01((progress - exitStart) / (1 - exitStart)),
          );
          const isEntering = progress <= entryEnd;
          const isHolding = progress > entryEnd && progress <= exitStart;
          const radiusX = isEntering
            ? lerp(1320, 650, entryPhase)
            : lerp(650, 1320, exitPhase);
          const radiusY = isEntering
            ? lerp(820, 350, entryPhase)
            : lerp(350, 820, exitPhase);
          const angleTurns = isEntering
            ? lerp(2.85, 2.25, entryPhase)
            : isHolding
              ? lerp(2.25, 0.2, holdPhase)
              : lerp(0.2, -0.72, exitPhase);
          const baseScale = isEntering
            ? lerp(1.24, 0.94, entryPhase)
            : lerp(0.94, 1.12, exitPhase);
          const entryOpacity = orbitEaseOut(clamp01(progress / 0.025));

          orbitSetters.forEach((motion) => {
            const angle = motion.slotAngle - ((angleTurns + orbitDriver.spin) * Math.PI * 2);
            const orbitX = Math.cos(angle) * radiusX;
            const orbitY = Math.sin(angle) * radiusY;
            const depth = Math.sin(angle);
            const depthScale = 0.88 + (((depth + 1) / 2) * 0.24);
            const scale = baseScale * depthScale;
            const z = depth * 220 * (1 + (exitPhase * 0.25));
            const entryTilt = (motion.order % 2 === 0 ? -1 : 1)
              * (10 + ((motion.order % 4) * 3));
            const rotation = isEntering
              ? lerp(entryTilt, 0, entryPhase)
              : 0;
            const opacity = entryOpacity * (1 - exitPhase);

            motion.setX(orbitX - motion.finalX);
            motion.setY(orbitY - motion.finalY);
            motion.setZ(z);
            motion.setScaleX(scale);
            motion.setScaleY(scale);
            motion.setRotation(rotation);
            motion.setOpacity(opacity);
          });
        };
      };

      media.add(
        {
          desktop: "(min-width: 901px)",
          reduceMotion: "(prefers-reduced-motion: reduce)",
        },
        (context) => {
          const { desktop, reduceMotion } = context.conditions;
          if (!desktop) return undefined;

          if (reduceMotion) {
            rotatingTextRef.current?.reset();
            gsap.set([graph, ...ringPlanes, ...rings, ...nodes, ...nodeContents, ...nodeIcons, ...nodeLabels, copy, portalLeaf, anythingScene, anythingStage, anythingTitle, rotatingWord, ...titleChars, ...initialRotatingChars, ...cards, ...baseCardImages], {
              clearProps: "all",
            });
            return undefined;
          }

          const finalGraphScale = () => 1;
          const cameraZoom = 14;
          const graphPortalCenter = { x: 720, y: 534.5 };
          const scaleSceneSize = { width: 1440, height: 903 };
          const graphSize = { width: 1440, height: 903 };
          const portalSize = { width: 158, height: 185 };
          const ringSizes = [
            { width: 334, height: 273 },
            { width: 560, height: 386 },
            { width: 786, height: 499 },
          ];
          const ringStartOverscan = [1.08, 1.035, 1.045];
          const ringCameraOffsets = [0, 0.12, 0.26];
          const ringCameraDurations = [1.16, 1.22, 1.28];
          const ringCameraZooms = [16.4, 15.2, cameraZoom];
          const portalCameraOffset = ringCameraOffsets[2];
          const portalCameraDuration = ringCameraDurations[2];
          const portalMaskOverscan = 2;
          const nodeRevealGroups = [
            [nodeContents[2], nodeContents[3]],
            [nodeContents[1], nodeContents[4]],
            [nodeContents[0], nodeContents[5]],
          ];
          const outerRingBaseline = 128 + ringSizes[2].height;
          const portalBaseline = 442 + portalSize.height;
          const portalGroundOffset = outerRingBaseline - portalBaseline;
          const portalGroundCorrection = portalGroundOffset * cameraZoom;
          const ringStartScales = ringSizes.map((size, index) => {
            const innerSilhouette = index === 0 ? portalSize : ringSizes[index - 1];
            const enclosingScale = Math.max(
              innerSilhouette.width / size.width,
              innerSilhouette.height / size.height,
            );
            return enclosingScale * ringStartOverscan[index];
          });
          const wordDriver = { progress: 0 };
          let activeWordIndex = 0;
          const renderRotatingWord = () => {
            const nextIndex = Math.min(
              anythingRotatingWords.length - 1,
              Math.max(0, Math.floor(wordDriver.progress + 0.0001)),
            );
            if (nextIndex === activeWordIndex) return;
            activeWordIndex = nextIndex;
            rotatingTextRef.current?.jumpTo(nextIndex);
          };
          const portalOffsetFromViewportCenter = {
            x: graphPortalCenter.x - (graphSize.width / 2),
            y: graphPortalCenter.y - (scaleSceneSize.height / 2),
          };

          const cameraDelta = () => ({
            x: -portalOffsetFromViewportCenter.x,
            y: -portalOffsetFromViewportCenter.y,
          });

          const syncPortalMask = () => {
            const sceneRect = anythingScene.getBoundingClientRect();
            const portalRect = portal.getBoundingClientRect();
            const sceneViewportScaleX = anythingScene.offsetWidth > 0
              ? sceneRect.width / anythingScene.offsetWidth
              : 1;
            const sceneViewportScaleY = anythingScene.offsetHeight > 0
              ? sceneRect.height / anythingScene.offsetHeight
              : 1;
            const position = `${(portalRect.left - sceneRect.left - portalMaskOverscan) / sceneViewportScaleX}px ${(portalRect.top - sceneRect.top - portalMaskOverscan) / sceneViewportScaleY}px`;
            const size = `${(portalRect.width + (portalMaskOverscan * 2)) / sceneViewportScaleX}px ${(portalRect.height + (portalMaskOverscan * 2)) / sceneViewportScaleY}px`;

            anythingScene.style.maskPosition = position;
            anythingScene.style.maskSize = size;
            anythingScene.style.webkitMaskPosition = position;
            anythingScene.style.webkitMaskSize = size;
          };

          gsap.set(graph, {
            x: 0,
            y: 0,
            xPercent: -50,
            scale: finalGraphScale,
            transformOrigin: `${graphPortalCenter.x}px ${graphPortalCenter.y}px`,
            force3D: false,
          });
          gsap.set(rings, {
            autoAlpha: 0,
            scaleX: (index) => ringStartScales[index],
            scaleY: (index) => ringStartScales[index],
            transformOrigin: "50% 100%",
            force3D: false,
          });
          ringPlanes.forEach((plane) => {
            const planeLeft = Number.parseFloat(getComputedStyle(plane).left);
            const planeTop = Number.parseFloat(getComputedStyle(plane).top);

            gsap.set(plane, {
              scaleX: 1,
              scaleY: 1,
              transformOrigin: `${graphPortalCenter.x - planeLeft}px ${graphPortalCenter.y - planeTop}px`,
              force3D: false,
            });
          });
          gsap.set(portal, {
            scale: 1,
            y: 0,
            transformOrigin: "50% 50%",
            force3D: false,
          });
          gsap.set(nodes, {
            autoAlpha: 1,
            y: 0,
            transformOrigin: "50% 50%",
            force3D: false,
          });
          gsap.set(nodeContents, {
            autoAlpha: 0,
            y: SCALE_NODE_VERTICAL_OFFSET,
            transformOrigin: "50% 50%",
            force3D: false,
          });
          gsap.set(nodeIcons, {
            autoAlpha: 1,
            scale: 1,
            transformOrigin: "50% 50%",
            force3D: false,
          });
          gsap.set(nodeLabels, { autoAlpha: 1, y: 0 });
          gsap.set(copy, { autoAlpha: 1, y: 0 });
          gsap.set(portal, {
            scale: 1,
            y: portalGroundOffset,
            transformOrigin: "50% 50%",
            force3D: false,
          });
          gsap.set(portalLeaf, {
            autoAlpha: 1,
            rotateY: 0,
            transformOrigin: "0% 50%",
            transformPerspective: HERO_PORTAL_LEAF_PERSPECTIVE,
            force3D: true,
          });
          gsap.set(anythingScene, {
            autoAlpha: 1,
            clipPath: "none",
          });
          syncPortalMask();
          gsap.set(anythingStage, {
            scale: 0.42,
            transformOrigin: "50% 50%",
            force3D: true,
          });
          rotatingTextRef.current?.reset();
          gsap.set(titleChars, { autoAlpha: 0, y: 40, force3D: true });
          gsap.set(rotatingWord, {
            autoAlpha: 0,
            clipPath: "inset(0 100% 0 0 round 10px)",
            transformOrigin: "0% 50%",
          });
          gsap.set(initialRotatingChars, { autoAlpha: 0, yPercent: 120, force3D: true });
          gsap.set(baseCardImages, {
            autoAlpha: 1,
            scale: 1,
            filter: "brightness(1) saturate(1)",
            transformOrigin: "50% 50%",
            force3D: true,
          });

          const orbitDriver = { progress: 0, spin: 0 };
          const renderCardOrbit = createCardOrbitRenderer(orbitDriver);

          cardMotion.forEach(({ card }) => {
            gsap.set(card, {
              autoAlpha: 1,
              x: 0,
              y: 0,
              z: 0,
              rotation: 0,
              scale: 1,
              transformOrigin: "50% 50%",
              force3D: true,
            });
          });
          renderCardOrbit();

          const archPrelude = gsap.timeline({
            defaults: { ease: "none" },
            scrollTrigger: {
              id: "scale-arch-prelude",
              trigger: section,
              start: SCALE_ARCH_REVEAL_START,
              end: "top 4%",
              scrub: SCALE_ARCH_REVEAL_SCRUB,
              invalidateOnRefresh: true,
              refreshPriority: -2,
            },
          });

          archPrelude
            .to(
              rings,
              {
                scaleX: 1,
                scaleY: 1,
                duration: 0.58,
                stagger: 0.42,
                ease: "power2.inOut",
              },
              0,
            )
            .to(
              rings,
              {
                autoAlpha: 1,
                duration: 0.12,
                stagger: 0.42,
                ease: "power2.out",
              },
              0.02,
            );

          const nodeRevealTimeline = gsap.timeline({
            defaults: { duration: 0.52, ease: "power3.out" },
            scrollTrigger: {
              id: "scale-node-reveal",
              trigger: section,
              start: "top 4%",
              toggleActions: "none none none none",
              onEnter: ({ animation }) =>
                animation.timeScale(1).play(),
              onLeaveBack: ({ animation }) =>
                animation.timeScale(1.8).reverse(),
              invalidateOnRefresh: true,
              refreshPriority: -1.5,
            },
          });

          nodeRevealGroups.forEach((group, index) => {
            nodeRevealTimeline.to(
              group,
              { autoAlpha: 1, y: 0 },
              index * 0.13,
            );
          });

          const settleNodeRevealForStickySequence = () => {
            nodeRevealTimeline.progress(1).pause();
          };

          const scrollSequence = gsap.timeline({
            defaults: { ease: "none" },
            onUpdate: syncPortalMask,
            onReverseComplete: () => {
              renderCardOrbit();
              syncPortalMask();
            },
            scrollTrigger: {
              id: "scale-to-anything-scroll",
              trigger: section,
              start: "top top",
              end: () => `+=${Math.round(window.innerHeight * 4.55)}`,
              scrub: ANYTHING_SCROLL_SCRUB,
              invalidateOnRefresh: true,
              refreshPriority: -1,
              onEnter: settleNodeRevealForStickySequence,
              onEnterBack: settleNodeRevealForStickySequence,
              onRefresh: () => {
                renderCardOrbit();
                syncPortalMask();
              },
            },
          });

          scrollSequence
            .addLabel("open", SCALE_PORTAL_OPEN_START)
            .to(copy, { autoAlpha: 0, y: 28, duration: 0.34, ease: "power2.in" }, "open")
            .to(
              nodes,
              {
                autoAlpha: 0,
                y: SCALE_NODE_VERTICAL_OFFSET,
                duration: 0.34,
                ease: "power2.in",
              },
              "open",
            )
            .to(
              portalLeaf,
              {
                rotateY: 90,
                transformPerspective: HERO_PORTAL_LEAF_PERSPECTIVE,
                duration: 1.1,
                ease: "power2.inOut",
                force3D: true,
              },
              "open",
            )
            .to(portalLeaf, { autoAlpha: 0, duration: 0.1, ease: "none" }, "open+=0.82")
            .addLabel("through", "open+=0.12")
            .to(
              graph,
              {
                x: () => cameraDelta().x,
                y: () => cameraDelta().y,
                duration: 1.5,
                ease: "power2.inOut",
                force3D: false,
              },
              "through",
            )
            .to(
              portal,
              {
                scale: cameraZoom,
                y: portalGroundCorrection,
                duration: portalCameraDuration,
                ease: "power2.inOut",
                force3D: false,
              },
              `through+=${portalCameraOffset}`,
            );

          ringPlanes.forEach((plane, index) => {
            scrollSequence.to(
              plane,
              {
                scaleX: ringCameraZooms[index],
                scaleY: ringCameraZooms[index],
                duration: ringCameraDurations[index],
                ease: "power2.inOut",
                force3D: false,
              },
              `through+=${ringCameraOffsets[index]}`,
            );
          });

          scrollSequence
            .to(
              anythingStage,
              { scale: 1, duration: 1.5, ease: "power2.inOut" },
              "through",
            )
            .addLabel("titleLead", "through+=0.58")
            .to(
              titleChars.slice(0, anythingTitlePrefixCharCount),
              {
                autoAlpha: 1,
                y: 0,
                duration: 0.34,
                stagger: 0.012,
                ease: "power3.out",
              },
              "titleLead",
            )
            .addLabel("wordReveal", "through+=1.16")
            .to(
              rotatingWord,
              {
                autoAlpha: 1,
                clipPath: "inset(0 0% 0 0 round 10px)",
                duration: 0.34,
                ease: "power3.out",
              },
              "wordReveal",
            )
            .to(
              initialRotatingChars,
              {
                autoAlpha: 1,
                yPercent: 0,
                duration: 0.36,
                stagger: 0.025,
                ease: "power3.out",
              },
              "wordReveal+=0.03",
            )
            .addLabel("titleSuffix", "wordReveal+=0.26")
            .to(
              titleChars.slice(anythingTitlePrefixCharCount),
              {
                autoAlpha: 1,
                y: 0,
                duration: 0.3,
                stagger: 0.012,
                ease: "power3.out",
              },
              "titleSuffix",
            )
            .addLabel("spiral", "through+=1.72");

          scrollSequence
            .to(
              orbitDriver,
              {
                progress: 0.78,
                duration: ANYTHING_ORBIT_ENTRY_DURATION,
                ease: "none",
                onUpdate: renderCardOrbit,
              },
              "spiral",
            )
            .addLabel("wordCycle", "through+=1.8")
            .to(
              orbitDriver,
              {
                spin: -0.78,
                duration: ANYTHING_WORD_BEAT * anythingRotatingWords.length,
                ease: "none",
                onUpdate: renderCardOrbit,
              },
              "wordCycle",
            )
            .to(
              wordDriver,
              {
                progress: anythingRotatingWords.length - 1,
                duration: ANYTHING_WORD_BEAT * (anythingRotatingWords.length - 1),
                ease: "none",
                onUpdate: renderRotatingWord,
              },
              "wordCycle",
            )
            .addLabel(
              "anythingExit",
              `wordCycle+=${ANYTHING_WORD_BEAT * anythingRotatingWords.length}`,
            )
            .to(
              orbitDriver,
              {
                progress: 1,
                spin: -1.12,
                duration: 0.62,
                ease: "none",
                onUpdate: renderCardOrbit,
              },
              "anythingExit",
            )
            .to(
              anythingTitle,
              {
                autoAlpha: 0,
                y: -34,
                duration: 0.48,
                ease: "power2.in",
              },
              "anythingExit+=0.06",
            );

          return undefined;
        },
      );

      media.add(
        {
          mobile: "(max-width: 900px)",
          reduceMotion: "(prefers-reduced-motion: reduce)",
        },
        (context) => {
          const { mobile, reduceMotion } = context.conditions;
          if (!mobile) return undefined;

          rotatingTextRef.current?.reset();
          gsap.set([anythingScene, anythingStage, anythingTitle, rotatingWord, ...titleChars, ...initialRotatingChars], {
            clearProps: "all",
          });

          if (reduceMotion) {
            gsap.set([...cards, ...baseCardImages], { clearProps: "all" });
            return undefined;
          }

          const mobileOrbitDriver = { progress: 0.5, spin: 0 };
          const renderMobileCardOrbit = createCardOrbitRenderer(mobileOrbitDriver);

          gsap.set(cards, {
            autoAlpha: 1,
            x: 0,
            y: 0,
            z: 0,
            rotation: 0,
            scale: 1,
            transformOrigin: "50% 50%",
            force3D: true,
          });
          gsap.set(baseCardImages, {
            autoAlpha: 1,
            scale: 1,
            filter: "brightness(1) saturate(1)",
            transformOrigin: "50% 50%",
            force3D: true,
          });
          renderMobileCardOrbit();

          const mobileOrbitTween = gsap.to(mobileOrbitDriver, {
            spin: -0.55,
            ease: "none",
            onUpdate: renderMobileCardOrbit,
            scrollTrigger: {
              id: "anything-mobile-orbit",
              trigger: anythingScene,
              start: "top bottom",
              end: "bottom top",
              scrub: 0.55,
              invalidateOnRefresh: true,
              refreshPriority: -0.5,
              onRefresh: renderMobileCardOrbit,
            },
          });

          return () => {
            mobileOrbitTween.scrollTrigger?.kill();
            mobileOrbitTween.kill();
          };
        },
      );

      return () => media.revert();
    },
    { scope: sectionRef },
  );

  return (
    <section ref={sectionRef} className="scale-journey easy-scale">
      <div
        className="landing-tracking-sentinel landing-tracking-sentinel--easy-scale"
        data-landing-screen="easy_scale"
        aria-hidden="true"
      />
      <div
        className="landing-tracking-sentinel landing-tracking-sentinel--anything"
        data-landing-screen="anything"
        aria-hidden="true"
      />
      <div className="scale-journey__visual">
        <div className="easy-scale__copy easy-scale__copy--mobile"
          data-landing-screen="easy_scale"
        >
          <h2 className="easy-scale__mobile-heading">
            {t("heading")}
          </h2>
          <p className="easy-scale__subtitle easy-scale__subtitle--mobile">
            {t("subtitle")}
          </p>
        </div>
        <div className="scale-scene-slot">
          <div className="scale-scene">
            <div className="scale-graph" aria-hidden="true">
              <span className="scale-arc-plane scale-arc-plane--large">
                <img className="scale-arc scale-arc--large" src={`${A}/scale-v2/arch-large.svg`} alt="" />
              </span>
              <span className="scale-arc-plane scale-arc-plane--medium">
                <img className="scale-arc scale-arc--medium" src={`${A}/scale-v2/arch-medium.svg`} alt="" />
              </span>
              <span className="scale-arc-plane scale-arc-plane--small">
                <img className="scale-arc scale-arc--small" src={`${A}/scale-v2/arch-small.svg`} alt="" />
              </span>
              <div className="scale-portal">
                <span className="scale-portal__mask">
                  <img className="scale-portal__leaf" src={`${A}/scale-v2/portal.svg`} alt="" />
                </span>
              </div>
              {scaleNodes.map((node) => (
                <div
                  className={`scale-node group absolute whitespace-nowrap font-semibold leading-normal text-black [transform-style:flat] ${node.positionClassName}`}
                  aria-label={t(`capabilities.${node.nameKey}`)}
                  aria-describedby={`scale-node-tooltip-${node.nameKey}`}
                  key={node.nameKey}
                  tabIndex={0}
                >
                  <span className="scale-node__content flex flex-col items-center justify-center gap-[2px]">
                    <span className={`scale-node__badge flex size-12 shrink-0 items-center justify-center overflow-hidden rounded-lg transition-transform duration-300 ease-[cubic-bezier(.16,1,.3,1)] will-change-transform motion-reduce:transition-none ${node.badgeClassName ?? "bg-white"}`}>
                      <img className={`block max-w-none ${node.iconClassName}`} src={`${A}/${node.icon}`} alt="" decoding="async" />
                    </span>
                    <span className={`scale-node__label ${node.labelClassName}`}>{t(`capabilities.${node.nameKey}`)}</span>
                  </span>
                  <span
                    className="scale-node__tooltip"
                    id={`scale-node-tooltip-${node.nameKey}`}
                    role="tooltip"
                  >
                    {t(`capabilityDescriptions.${node.nameKey}`)}
                  </span>
                </div>
              ))}
            </div>
            <div className="easy-scale__copy easy-scale__copy--desktop absolute left-1/2 top-[644px] flex h-28 w-max max-w-[1440px] -translate-x-1/2 flex-col items-center overflow-hidden text-center [word-break:break-word]">
              <h2 className="w-max whitespace-nowrap text-center font-['Google_Sans_Flex'] text-[calc(64px-var(--landing-v2-locale-font-offset))] font-semibold leading-[61.281px] tracking-[-1.7023px] text-slate-950">
                {t("heading")}
              </h2>
              <p className="easy-scale__subtitle w-max whitespace-nowrap text-center font-['Google_Sans_Flex'] text-[calc(20px-var(--landing-v2-locale-font-offset))] font-normal leading-[22px] tracking-[-0.5px] text-zinc-950">
                {t("subtitle")}
              </p>
            </div>
          </div>
        </div>

        <div className="anything-scene">
          <div
            className="landing-tracking-mobile-anchor"
            data-landing-screen="anything"
            aria-hidden="true"
          />
          <div className="anything-scene__mobile-copy">
            <h2>{t("anythingMobileTitle")}</h2>
            <button
              type="button"
              className="anything-scene__mobile-cta"
              onClick={() => onBuildAction?.("apps", "anything")}
              data-landing-button-id="anything.mobile_cta"
              data-landing-button-screen="anything"
              data-landing-action="login"
              data-landing-creation-type="app"
              data-landing-triggers-login="true"
            >
              <span>{t("anythingCta")}</span>
              <ArrowRight aria-hidden="true" size={30} strokeWidth={1.5} />
            </button>
          </div>
          <div className="anything-scene__stage">
            <div className="anything__collage" aria-hidden="true">
              {anythingCollage.map((file, index) => (
                <div className={`anything__image anything__image--${index + 1}`} key={file}>
                  <img className="anything__image-base" src={`${A}/${file}`} alt="" loading="eager" decoding="async" />
                </div>
              ))}
            </div>
          </div>
          <h2
            className="anything-scene__title"
            aria-label={`${anythingTitlePrefix}${anythingRotatingWords[anythingWordIndex]}${anythingTitleSuffix}`}
          >
            {renderAnythingTitleCharacters(anythingTitlePrefix)}
            <button
              type="button"
              className="anything-scene__rotating-word"
              aria-label={anythingRotatingWords[anythingWordIndex]}
              style={{ backgroundColor: ANYTHING_ROTATING_COLORS[anythingWordIndex] }}
              onClick={() => onBuildAction?.("apps", "anything")}
              data-landing-button-id="anything.rotating_word"
              data-landing-button-screen="anything"
              data-landing-action="login"
              data-landing-creation-type="app"
              data-landing-triggers-login="true"
            >
              <RotatingText
                ref={rotatingTextRef}
                texts={anythingRotatingWords}
                auto={false}
                loop={false}
                animatePresenceMode="popLayout"
                staggerFrom="first"
                staggerDuration={0.014}
                initial={ANYTHING_ROTATING_INITIAL}
                animate={ANYTHING_ROTATING_ANIMATE}
                exit={ANYTHING_ROTATING_EXIT}
                transition={ANYTHING_ROTATING_TRANSITION}
                onNext={setAnythingWordIndex}
                splitLevelClassName="anything-scene__rotating-split"
                elementLevelClassName="anything-scene__rotating-element"
              />
            </button>
            {renderAnythingTitleCharacters(anythingTitleSuffix, anythingTitlePrefixCharCount)}
          </h2>
        </div>
      </div>
    </section>
  );
}

const businessCards = [
  {
    titleKey: "templatesTitle",
    video: "business/pro-templates.mp4",
    poster: "business/pro-templates.png?v=b6b590f2",
    copyKey: "templatesDescription",
    ctaKey: "templatesCta",
    visual: "templates",
    href: "https://enter.converge.ai/marketplace/design-kit",
  },
  {
    titleKey: "capabilitiesTitle",
    video: "business/specialized-capabilities.mp4",
    poster: "business/specialized-capabilities.png",
    copyKey: "capabilitiesDescription",
    ctaKey: "capabilitiesCta",
    visual: "capabilities",
    href: "https://enter.converge.ai/agent-builder",
  },
  {
    titleKey: "expertTitle",
    video: "business/expert.mp4",
    poster: "business/expert.png",
    copyKey: "expertDescription",
    ctaKey: "expertCta",
    visual: "expert",
    href: "https://enter.converge.ai/contact-sales",
  },
];

function playBusinessCardVideo(event) {
  if (!window.matchMedia("(hover: hover) and (pointer: fine) and (prefers-reduced-motion: no-preference)").matches) return;

  const video = event.currentTarget.querySelector("video");
  if (!video) return;

  if (video.readyState >= 1) video.currentTime = 0;
  void video.play().catch(() => undefined);
}

function resetBusinessCardVideo(event) {
  const video = event.currentTarget.querySelector("video");
  if (!video) return;

  video.pause();
  if (video.readyState >= 1) video.currentTime = 0;
}

function Business() {
  const sectionRef = useRef(null);
  const [headingPlay, setHeadingPlay] = useState(false);
  const t = useTranslations("landingV2.business");
  const heading = t("heading");
  const smartWrapHeading = shouldUseSmartWrap(heading);

  useRisingSection({
    sectionRef,
    contentSelector: ".business__visual",
    headingReveal: setHeadingPlay,
    y: 88,
  });

  useGSAP(
    (context, contextSafe) => {
      const section = sectionRef.current;
      if (!section) return undefined;

      const grid = section.querySelector(".business__grid");
      const cards = [...section.querySelectorAll(".business-card")];
      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (!grid || cards.length !== 3) return undefined;

      if (prefersReducedMotion) {
        gsap.set(cards, { autoAlpha: 1, y: 0, clearProps: "transform,opacity,visibility,willChange" });
        return undefined;
      }

      gsap.set(cards, { autoAlpha: 0, y: 64, willChange: "transform, opacity" });

      const revealCards = contextSafe(() => {
        const revealTimeline = gsap.timeline({
          onComplete: () => {
            gsap.set(cards, { clearProps: "transform,opacity,visibility,willChange" });
          },
        });

        revealTimeline
          .to(cards, { y: 0, duration: 0.85, ease: "power3.out" }, 0)
          .to(cards, { autoAlpha: 1, duration: 0.65, stagger: 0.18, ease: "power2.out" }, 0);
      });

      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          revealCards();
        },
        {
          root: null,
          rootMargin: "0px 0px -18% 0px",
          threshold: 0,
        },
      );

      observer.observe(grid);

      return () => observer.disconnect();
    },
    { scope: sectionRef },
  );

  return (
    <section ref={sectionRef} className="business" id="resources"
      data-landing-screen="business"
    >
      <div className="business__visual">
        <div className="business__heading">
          <AnimatedHeading
            text={heading}
            manual
            play={headingPlay}
            smartWrap={smartWrapHeading}
          />
          <p className="business__subtitle">{t("subtitle")}</p>
        </div>
        <div className="business__grid">
          {businessCards.map((card, index) => {
            const title = card.titleKey ? t(card.titleKey) : card.title;
            const copy = card.copyKey ? t(card.copyKey) : card.copy;
            const visual = card.visual || ["agents", "experts", "templates"][index];

            return (
              <button
                type="button"
                className={`business-card business-card--${visual}`}
                onClick={() => window.location.assign(card.href)}
                data-landing-button-id={`business.card.${visual}`}
                data-landing-button-screen="business"
                data-landing-action="navigate"
                data-landing-content-key={visual}
                data-landing-destination={visual}
                key={title}
              >
                <div
                  className={`business-card__image business-card__image--${visual}`}
                  onPointerEnter={playBusinessCardVideo}
                  onPointerLeave={resetBusinessCardVideo}
                  onPointerCancel={resetBusinessCardVideo}
                >
                  <video
                    src={`${A}/${card.video}`}
                    poster={`${A}/${card.poster}`}
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    aria-hidden="true"
                  />
                </div>
                {card.ctaKey ? (
                  <div className="business-card__details mt-5 flex flex-col items-start gap-5 font-['Google_Sans_Flex']">
                    <div className="business-card__copy flex flex-col items-start gap-1">
                      <h3 className="!mt-0 !text-[calc(32px-var(--landing-v2-locale-font-offset))] !leading-9 !tracking-[-0.64px] !text-black">{title}</h3>
                      <p className="!mt-0 !w-full !max-w-[360px] !text-[calc(16px-var(--landing-v2-locale-font-offset))] !leading-[18.652px] !text-black">{copy}</p>
                    </div>
                    <span className="business-card__cta inline-flex h-10 items-center justify-center gap-1.5 overflow-hidden rounded-[10px] bg-black px-2.5 text-[calc(16px-var(--landing-v2-locale-font-offset))] font-normal leading-9 tracking-[-0.32px] whitespace-nowrap text-white">
                      <BusinessCtaMotionLabel label={t(card.ctaKey)} />
                      <ArrowRight aria-hidden="true" size={16} strokeWidth={1.33} />
                    </span>
                  </div>
                ) : (
                  <>
                    <h3>{title}</h3>
                    <p>{copy}</p>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const TESTIMONIAL_CARDS = [
  { key: "realEstate", className: "gray", avatar: "feedback/real-estate.png" },
  { key: "productDesign", className: "yellow", avatar: "feedback/product-design.png" },
  { key: "devOps", className: "pink", avatar: "feedback/devops.png" },
  { key: "education", className: "purple", avatar: "feedback/education.png" },
  { key: "enterpriseAi", className: "orange", avatar: "feedback/enterprise-ai.png" },
  { key: "interactiveMedia", className: "blue", avatar: "feedback/interactive-media.png" },
  { key: "aiEntertainment", className: "yellow", avatar: "feedback/ai-entertainment.png" },
  { key: "productDevelopment", className: "pink", avatar: "feedback/product-development.png" },
  { key: "financeOperations", className: "purple", avatar: "feedback/finance-operations.png" },
  { key: "sustainableRetail", className: "orange", avatar: "feedback/sustainable-retail.png" },
];

function Feedback() {
  const t = useTranslations("landingV2.feedback");
  const sectionRef = useRef(null);
  const [headingPlay, setHeadingPlay] = useState(false);

  useRisingSection({
    sectionRef,
    contentSelector: ".feedback__visual",
    headingReveal: setHeadingPlay,
  });

  useGSAP(
    () => {
      const section = sectionRef.current;
      if (!section) return undefined;

      const track = section.querySelector(".feedback__track");
      const cards = [...section.querySelectorAll(".quote-card")];
      const firstCard = cards[0];
      const firstClone = cards[TESTIMONIAL_CARDS.length];
      const media = gsap.matchMedia();
      if (!track || !firstCard || !firstClone) return undefined;

      media.add("(prefers-reduced-motion: no-preference)", () => {
        const cardEntrance = gsap.fromTo(
          cards,
          {
            autoAlpha: 0,
            y: 64,
            willChange: "transform, opacity",
          },
          {
            autoAlpha: 1,
            y: 0,
            duration: 0.85,
            ease: "power3.out",
            stagger: (index) => (index % TESTIMONIAL_CARDS.length) * 0.08,
            clearProps: "transform,opacity,visibility,willChange",
            paused: true,
          },
        );

        const entranceObserver = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            entranceObserver.disconnect();
            cardEntrance.play();
          },
          {
            root: null,
            rootMargin: "0px 0px -28% 0px",
            threshold: 0,
          },
        );

        entranceObserver.observe(track);

        return () => {
          entranceObserver.disconnect();
          cardEntrance.kill();
        };
      });

      media.add(
        "(min-width: 901px) and (prefers-reduced-motion: no-preference)",
        () => {
          const loopDistance = firstClone.offsetLeft - firstCard.offsetLeft;
          if (loopDistance <= 0) return undefined;

          let isVisible = false;
          const loop = gsap.fromTo(track, {
            x: -loopDistance,
          }, {
            x: 0,
            duration: loopDistance / 38,
            ease: "none",
            repeat: -1,
            paused: true,
          });

          const updatePlayback = () => {
            if (isVisible) loop.resume();
            else loop.pause();
          };

          const visibilityObserver = new IntersectionObserver((entries) => {
            isVisible = entries.some((entry) => entry.isIntersecting);
            updatePlayback();
          });

          visibilityObserver.observe(section);

          return () => visibilityObserver.disconnect();
        },
      );

      return () => media.revert();
    },
    { scope: sectionRef },
  );

  const carouselItems = [
    ...TESTIMONIAL_CARDS,
    ...TESTIMONIAL_CARDS,
    ...TESTIMONIAL_CARDS,
  ];

  return (
    <section ref={sectionRef} className="feedback" id="community"
      data-landing-screen="feedback"
    >
      <div className="feedback__visual">
        <AnimatedHeading
          text={t("heading")}
          manual
          play={headingPlay}
        />
        <div className="feedback__track" aria-label={t("ariaLabel")}>
          {carouselItems.map((item, index) => {
            const isClone = index >= TESTIMONIAL_CARDS.length;
            return (
              <article
                className={`quote-card quote-card--${item.className}`}
                data-clone={isClone ? "true" : undefined}
                aria-hidden={isClone || undefined}
                tabIndex={isClone ? undefined : 0}
                key={`${item.key}-${index}`}
              >
                <div className="quote-card__top">
                  <blockquote>{t(`items.${item.key}.quote`)}</blockquote>
                  <img src={`${A}/${item.avatar}`} alt="" loading="lazy" decoding="async" />
                </div>
                <div className="quote-card__person">
                  <strong>{t(`items.${item.key}.name`)}</strong>
                  <span>{t(`items.${item.key}.role`)}</span>
                </div>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

const FAQ_ITEM_KEYS = [
  "whatIsEnter",
  "whatCanBuild",
  "codingExperience",
  "startFromScratch",
  "hosting",
  "ownCode",
  "buildAiAgent",
  "collaborate",
  "isFree",
  "privacy",
  "editAfterLive",
];

const FAQ_CONTACT_EMAIL = "support@enter.pro";

function FAQ() {
  const [openIndex, setOpenIndex] = useState(null);
  const sectionRef = useRef(null);
  const [headingPlay, setHeadingPlay] = useState(false);
  const t = useTranslations("faq");
  const faqItems = FAQ_ITEM_KEYS.map((key) => ({
    key,
    question: t(`items.${key}.question`),
    answer: t(`items.${key}.answer`),
  }));

  useRisingSection({
    sectionRef,
    contentSelector: ".faq__visual",
    headingReveal: setHeadingPlay,
  });

  return (
    <section ref={sectionRef} className="faq" id="pricing"
      data-landing-screen="faq"
    >
      <div className="faq__visual">
        <div className="faq__heading">
          <AnimatedHeading text={t("title")} manual play={headingPlay} />
          <p className="faq__description">
            {t.rich("description", {
              email: FAQ_CONTACT_EMAIL,
              link: (chunks) => (
                <a className="faq__email-link" href={`mailto:${FAQ_CONTACT_EMAIL}`}
                  data-landing-button-id="faq.contact_email"
                  data-landing-button-screen="faq"
                  data-landing-action="navigate"
                  data-landing-destination="support-email"
                >
                  {chunks}
                </a>
              ),
            })}
          </p>
        </div>
        <div className="faq__list">
          {faqItems.map(({ key, question, answer }, index) => {
            const isOpen = openIndex === index;
            const questionId = `faq-question-${index}`;
            const answerId = `faq-answer-${index}`;
            return (
              <div className={`faq-item${isOpen ? " is-open" : ""}`} key={key}>
                <button
                  id={questionId}
                  type="button"
                  aria-controls={answerId}
                  aria-expanded={isOpen}
                  onClick={() => setOpenIndex(isOpen ? null : index)}
                  data-landing-button-id={`faq.item.${key}`}
                  data-landing-button-screen="faq"
                  data-landing-action="toggle"
                  data-landing-content-key={key}
                >
                  <span className="faq-item__question">{question}</span>
                  <span className="faq-item__dot" aria-hidden="true" />
                </button>
                <div
                  id={answerId}
                  className="faq-item__answer"
                  role="region"
                  aria-hidden={!isOpen}
                  aria-labelledby={questionId}
                >
                  <div className="faq-item__answer-inner">
                    <p>{answer}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function GradientCTA({ onGetStarted }) {
  const sectionRef = useRef(null);
  const [headingPlay, setHeadingPlay] = useState(false);
  const tGradientCta = useTranslations("landingV2.gradientCta");
  const tFooterHero = useTranslations("footerHero");
  const tCta = useTranslations("cta");

  useRisingSection({
    sectionRef,
    contentSelector: ".gradient-cta__content",
    headingReveal: setHeadingPlay,
  });

  return (
    <section ref={sectionRef} className="gradient-cta"
      data-landing-screen="final_cta"
    >
      <div className="gradient-cta__content">
        <AnimatedHeading text={tGradientCta("heading")} manual play={headingPlay} />
        <p>{tFooterHero("subtitle")}</p>
        <button type="button" onClick={onGetStarted}
          data-landing-button-id="final_cta.get_started"
          data-landing-button-screen="final_cta"
          data-landing-action="login"
          data-landing-creation-type="app"
          data-landing-triggers-login="true"
        >
          {tCta("getStartedFree")}
        </button>
      </div>
    </section>
  );
}

const FOOTER_BOUNCE_DOWN = "M0 0C0 0 .203472 .17284 .5 .17284S1 0 1 0V1H0Z";
const FOOTER_BOUNCE_FLAT = "M0 0C0 0 .203472 0 .5 0S1 0 1 0V1H0Z";
const FOOTER_PEEK_HEIGHT = 12;

function BrandEndingLogo({ animationControlRef }) {
  const logoRef = useRef(null);
  const animationContainerRef = useRef(null);
  const animationRef = useRef(null);
  const pendingPlayRef = useRef(false);
  const reducedMotionRef = useRef(false);

  useEffect(() => {
    const logo = logoRef.current;
    const container = animationContainerRef.current;
    if (!logo || !container) return undefined;

    let disposed = false;
    const abortController = new AbortController();
    const motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    reducedMotionRef.current = motionQuery.matches;

    const stopOnLastFrame = () => {
      const animation = animationRef.current;
      if (!animation) return;
      animation.goToAndStop(Math.max(animation.totalFrames - 1, 0), true);
    };
    const playOnce = () => {
      if (reducedMotionRef.current) return;
      const animation = animationRef.current;
      if (!animation) {
        pendingPlayRef.current = true;
        return;
      }
      pendingPlayRef.current = false;
      animation.goToAndPlay(0, true);
    };
    const controller = { playOnce };
    animationControlRef.current = controller;

    const handleMotionPreferenceChange = (event) => {
      reducedMotionRef.current = event.matches;
      if (event.matches) {
        pendingPlayRef.current = false;
        stopOnLastFrame();
      }
    };
    motionQuery.addEventListener("change", handleMotionPreferenceChange);

    Promise.all([
      import("lottie-web/build/player/lottie_light_canvas"),
      fetch(`${A}/final-footer/converge.json`, {
        cache: "force-cache",
        signal: abortController.signal,
      }).then((response) => {
        if (!response.ok) throw new Error(`Unable to load Final Brand animation (${response.status})`);
        return response.json();
      }),
    ]).then(([lottieModule, animationData]) => {
      if (disposed) return;

      const lottie = lottieModule.default ?? lottieModule;
      const animation = lottie.loadAnimation({
        container,
        renderer: "canvas",
        loop: false,
        autoplay: false,
        animationData,
        rendererSettings: {
          clearCanvas: true,
          dpr: Math.min(window.devicePixelRatio || 1, 3),
        },
      });
      animationRef.current = animation;

      animation.addEventListener("DOMLoaded", () => {
        if (disposed) return;
        logo.classList.add("is-ready");
        if (reducedMotionRef.current) {
          stopOnLastFrame();
        } else if (pendingPlayRef.current) {
          playOnce();
        } else {
          animation.goToAndStop(0, true);
        }
      });
      animation.addEventListener("complete", stopOnLastFrame);
    }).catch((error) => {
      if (error?.name !== "AbortError") pendingPlayRef.current = false;
    });

    return () => {
      disposed = true;
      abortController.abort();
      motionQuery.removeEventListener("change", handleMotionPreferenceChange);
      animationRef.current?.destroy();
      animationRef.current = null;
      logo.classList.remove("is-ready");
      if (animationControlRef.current === controller) animationControlRef.current = null;
    };
  }, [animationControlRef]);

  const playOnHover = () => {
    if (!window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    animationControlRef.current?.playOnce();
  };

  return (
    <div
      ref={logoRef}
      className="brand-ending__loader"
      aria-hidden="true"
      onPointerEnter={playOnHover}
    >
      <div ref={animationContainerRef} className="brand-ending__lottie" />
      <img
        className="brand-ending__loader-fallback"
        src={`${A}/final-footer/enter-loader.svg`}
        alt=""
      />
    </div>
  );
}

function FinalBrandPage({ bouncePathRef, logoAnimationRef }) {
  const t = useTranslations("landingV2.finalBrand");

  return (
    <section className="brand-ending" aria-labelledby="brand-ending-title">
      <svg className="brand-ending__clip" width="0" height="0" aria-hidden="true">
        <defs>
          <clipPath id="brand-ending-clip" clipPathUnits="objectBoundingBox">
            <path ref={bouncePathRef} d={FOOTER_BOUNCE_FLAT} />
          </clipPath>
        </defs>
      </svg>
      <div className="brand-ending__surface">
        <DotField
          className="brand-ending__dot-field"
          aria-hidden="true"
          dotRadius={1.5}
          dotSpacing={14}
          bulgeStrength={67}
          glowRadius={160}
          sparkle={false}
          waveAmplitude={0}
          gradientFrom="rgba(255, 193, 87, 0.72)"
          gradientTo="rgba(213, 89, 255, 0.62)"
          glowColor="rgba(18, 15, 23, 0.96)"
        />
      </div>
      <div className="brand-ending__stage">
        <BrandEndingLogo animationControlRef={logoAnimationRef} />
        <h2 id="brand-ending-title" className="brand-ending__headline">
          {t("titleLine1")}<br />
          {t.rich("titleLine2", {
            em: (chunks) => <span>{chunks}</span>,
          })}
        </h2>
        <div className="brand-ending__products" aria-label={t("productsAriaLabel")}>
          <span className="brand-ending__product">
            <img src={`${A}/final-footer/framia.svg`} alt="Framia" />
          </span>
          <span className="brand-ending__product">
            <img src={`${A}/final-footer/partner-mark.svg`} alt="" />
          </span>
          <span className="brand-ending__product">
            <img src={`${A}/final-footer/combos.svg`} alt="Combos" />
          </span>
          <span className="brand-ending__product">
            <img src={`${A}/final-footer/concat.svg?v=69ac3211`} alt="Concat" />
          </span>
        </div>
      </div>
    </section>
  );
}

function FinalReveal({ onGetStarted, footer }) {
  const revealRef = useRef(null);
  const slotRef = useRef(null);
  const bouncePathRef = useRef(null);
  const logoAnimationRef = useRef(null);

  useGSAP(
    (context, contextSafe) => {
      const reveal = revealRef.current;
      const slot = slotRef.current;
      const footer = reveal?.querySelector(".brand-ending");
      const bouncePath = bouncePathRef.current;
      const header = document.querySelector(".landing-v2-header");
      if (!reveal || !slot || !footer || !bouncePath || !header) return undefined;

      const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const transition = prefersReducedMotion ? 0 : 0.28;
      const footerLoader = footer.querySelector(".brand-ending__loader");
      const footerHeadline = footer.querySelector(".brand-ending__headline");
      const footerProducts = gsap.utils.toArray(".brand-ending__product", footer);
      const footerContent = [footerLoader, footerHeadline, ...footerProducts].filter(Boolean);
      const resetFooterContent = () => {
        gsap.set(footerLoader, { autoAlpha: 0, y: 36 });
        gsap.set(footerHeadline, { autoAlpha: 0, y: 48 });
        gsap.set(footerProducts, { autoAlpha: 0, y: 40 });
      };
      const playFooterContent = () => {
        logoAnimationRef.current?.playOnce();
        gsap.killTweensOf(footerContent);
        if (prefersReducedMotion) {
          gsap.set(footerContent, { autoAlpha: 1, y: 0 });
          return;
        }

        gsap.timeline({ defaults: { ease: "power3.out", overwrite: true } })
          .to(footerLoader, { autoAlpha: 1, y: 0, duration: 0.56 }, 0.08)
          .to(footerHeadline, { autoAlpha: 1, y: 0, duration: 0.68 }, 0.18)
          .to(footerProducts, {
            autoAlpha: 1,
            y: 0,
            duration: 0.58,
            stagger: 0.07,
          }, 0.34);
      };
      const settleSurface = (velocity = 0) => {
        if (prefersReducedMotion) {
          gsap.set(bouncePath, { attr: { d: FOOTER_BOUNCE_FLAT } });
          return;
        }

        const variation = gsap.utils.clamp(0, 0.62, Math.abs(velocity) / 10000);
        gsap.fromTo(
          bouncePath,
          { attr: { d: FOOTER_BOUNCE_DOWN } },
          {
            attr: { d: FOOTER_BOUNCE_FLAT },
            duration: 2,
            ease: `elastic.out(${1 + variation}, ${1 - variation})`,
            overwrite: true,
          },
        );
      };

      let footerState = "hidden";
      let gateArmed = false;
      let armTimer = null;
      let touchStartY = 0;
      let revealedScrollTop = 0;

      const isAtDocumentEnd = () => (
        window.scrollY >= document.documentElement.scrollHeight - window.innerHeight - 2
      );
      const clearArmTimer = () => {
        if (armTimer !== null) window.clearTimeout(armTimer);
        armTimer = null;
      };
      const disarmGate = () => {
        clearArmTimer();
        gateArmed = false;
      };
      const armAfterScrollStops = () => {
        clearArmTimer();
        armTimer = window.setTimeout(() => {
          gateArmed = footerState === "hidden" && isAtDocumentEnd();
          armTimer = null;
        }, 240);
      };

      const revealFooter = contextSafe((velocity = 0) => {
        if (footerState !== "hidden") return;
        footerState = "revealing";
        disarmGate();
        footer.setAttribute("aria-hidden", "false");
        settleSurface(velocity);
        playFooterContent();
        gsap.to(header, { autoAlpha: 0, y: -80, duration: transition, ease: "power2.out", overwrite: true });
        const startScroll = window.scrollY;
        const targetHeight = footer.offsetHeight;
        const heightDelta = targetHeight - FOOTER_PEEK_HEIGHT;
        const revealProgress = { value: 0 };
        gsap.to(revealProgress, {
          value: 1,
          duration: prefersReducedMotion ? 0 : 0.85,
          ease: "power3.out",
          overwrite: true,
          onUpdate: () => {
            const revealedHeight = FOOTER_PEEK_HEIGHT + heightDelta * revealProgress.value;
            slot.style.height = `${revealedHeight}px`;
            window.scrollTo(0, startScroll + heightDelta * revealProgress.value);
          },
          onComplete: () => {
            slot.style.height = `${targetHeight}px`;
            window.scrollTo(0, document.documentElement.scrollHeight - window.innerHeight);
            revealedScrollTop = window.scrollY;
            footerState = "visible";
          },
        });
      });

      const hideFooter = contextSafe(() => {
        if (footerState !== "visible") return;
        footerState = "hiding";
        disarmGate();
        gsap.killTweensOf(bouncePath);
        gsap.killTweensOf(footerContent);
        gsap.to(footerContent, {
          autoAlpha: 0,
          y: 32,
          duration: prefersReducedMotion ? 0 : 0.22,
          ease: "power2.in",
          overwrite: true,
        });
        gsap.set(bouncePath, { attr: { d: FOOTER_BOUNCE_FLAT } });
        gsap.to(header, { autoAlpha: 1, y: 0, duration: transition, ease: "power2.out", overwrite: true });
        slot.style.height = `${footer.offsetHeight}px`;
        window.scrollTo(0, revealedScrollTop);
        const startScroll = window.scrollY;
        const startHeight = slot.offsetHeight;
        const heightDelta = startHeight - FOOTER_PEEK_HEIGHT;
        const hideProgress = { value: 0 };
        gsap.to(hideProgress, {
          value: 1,
          duration: prefersReducedMotion ? 0 : 0.72,
          ease: "power3.inOut",
          overwrite: true,
          onUpdate: () => {
            const remainingHeight = startHeight - heightDelta * hideProgress.value;
            slot.style.height = `${remainingHeight}px`;
            window.scrollTo(0, startScroll - heightDelta * hideProgress.value);
          },
          onComplete: () => {
            slot.style.height = `${FOOTER_PEEK_HEIGHT}px`;
            footerState = "hidden";
            footer.setAttribute("aria-hidden", "true");
            if (!prefersReducedMotion) resetFooterContent();
          },
        });
      });

      const handleScroll = () => {
        if (footerState !== "hidden") return;
        if (isAtDocumentEnd()) {
          armAfterScrollStops();
        } else {
          disarmGate();
        }
      };
      const handleWheel = (event) => {
        if (footerState === "revealing" || footerState === "hiding") {
          event.preventDefault();
          return;
        }

        if (footerState === "visible") {
          if (event.deltaY < -4 && window.scrollY <= revealedScrollTop + 2) {
            event.preventDefault();
            hideFooter();
          }
          return;
        }

        if (!isAtDocumentEnd() || event.deltaY <= 0) {
          disarmGate();
          return;
        }

        event.preventDefault();
        if (gateArmed) revealFooter(Math.abs(event.deltaY) * 60);
        else armAfterScrollStops();
      };
      const handleTouchStart = (event) => {
        touchStartY = event.touches[0]?.clientY ?? 0;
      };
      const handleTouchMove = (event) => {
        const currentY = event.touches[0]?.clientY ?? touchStartY;
        const delta = touchStartY - currentY;

        if (footerState === "revealing" || footerState === "hiding") {
          event.preventDefault();
          return;
        }
        if (footerState === "visible") {
          if (delta < -12 && window.scrollY <= revealedScrollTop + 2) {
            event.preventDefault();
            hideFooter();
          }
          return;
        }
        if (isAtDocumentEnd() && gateArmed && delta > 12) {
          event.preventDefault();
          revealFooter(Math.abs(delta) * 80);
        }
      };
      const handleTouchEnd = () => {
        if (footerState === "hidden" && isAtDocumentEnd()) armAfterScrollStops();
      };
      const handleKeyDown = (event) => {
        const downKeys = ["ArrowDown", "PageDown", "End", " "];
        const upKeys = ["ArrowUp", "PageUp", "Home"];

        if (
          footerState === "visible"
          && upKeys.includes(event.key)
          && window.scrollY <= revealedScrollTop + 2
        ) {
          event.preventDefault();
          hideFooter();
          return;
        }
        if (footerState === "hidden" && gateArmed && isAtDocumentEnd() && downKeys.includes(event.key)) {
          event.preventDefault();
          revealFooter(2600);
        }
      };

      footer.setAttribute("aria-hidden", "true");
      gsap.set(slot, { height: FOOTER_PEEK_HEIGHT });
      if (prefersReducedMotion) gsap.set(footerContent, { autoAlpha: 1, y: 0 });
      else resetFooterContent();
      window.addEventListener("scroll", handleScroll, { passive: true });
      window.addEventListener("wheel", handleWheel, { passive: false });
      window.addEventListener("touchstart", handleTouchStart, { passive: true });
      window.addEventListener("touchmove", handleTouchMove, { passive: false });
      window.addEventListener("touchend", handleTouchEnd, { passive: true });
      window.addEventListener("keydown", handleKeyDown);
      handleScroll();

      return () => {
        clearArmTimer();
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("wheel", handleWheel);
        window.removeEventListener("touchstart", handleTouchStart);
        window.removeEventListener("touchmove", handleTouchMove);
        window.removeEventListener("touchend", handleTouchEnd);
        window.removeEventListener("keydown", handleKeyDown);
        gsap.killTweensOf(bouncePath);
        gsap.killTweensOf(footerContent);
        gsap.set(footerContent, { clearProps: "opacity,visibility,transform" });
        gsap.set(bouncePath, { clearProps: "all" });
        gsap.set(slot, { clearProps: "height" });
        gsap.set(header, { clearProps: "opacity,visibility,transform" });
      };
    },
    { scope: revealRef },
  );

  return (
    <div ref={revealRef} className="final-reveal">
      <GradientCTA onGetStarted={onGetStarted} />
      {footer}
      <div ref={slotRef} className="final-reveal__slot">
        <FinalBrandPage
          bouncePathRef={bouncePathRef}
          logoAnimationRef={logoAnimationRef}
        />
      </div>
    </div>
  );
}

export function LandingV2({
  header,
  footer,
  prompt,
  announcement,
  semanticHeading,
  onGetStarted,
  onPortalClick,
  onBuildAction,
}) {
  const afterHeroRef = useRef(null);
  const [introComplete, setIntroComplete] = useState(false);
  const [pageReady, setPageReady] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;

    root.classList.toggle("hero-intro-active", !pageReady);
    body.classList.toggle("hero-intro-active", !pageReady);

    if (!pageReady && !window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }

    return () => {
      root.classList.remove("hero-intro-active");
      body.classList.remove("hero-intro-active");
    };
  }, [pageReady]);

  useGSAP(
    (context, contextSafe) => {
      const content = afterHeroRef.current;
      if (!content) return undefined;

      if (!introComplete) {
        gsap.set(content, { autoAlpha: 0, y: 40 });
        return undefined;
      }

      const finishReveal = contextSafe(() => {
        gsap.set(content, { clearProps: "transform,will-change" });
        setPageReady(true);
        window.requestAnimationFrame(() => ScrollTrigger.refresh());
      });

      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        gsap.set(content, { autoAlpha: 1, y: 0 });
        finishReveal();
        return undefined;
      }

      gsap.set(content, { willChange: "transform, opacity" });
      gsap.fromTo(
        content,
        { autoAlpha: 0, y: 40 },
        {
          autoAlpha: 1,
          y: 0,
          duration: 0.7,
          ease: "power3.out",
          onComplete: finishReveal,
        },
      );

      return undefined;
    },
    {
      scope: afterHeroRef,
      dependencies: [introComplete],
      revertOnUpdate: true,
    },
  );

  return (
    <main className={`home-landing-v2 page-shell${pageReady ? "" : " page-shell--intro"}`}>
      <h1 className="sr-only">{semanticHeading}</h1>
      {header({ introComplete })}
      <Hero
        onIntroComplete={setIntroComplete}
        prompt={prompt}
        announcement={announcement}
        onPortalClick={onPortalClick}
      />
      {introComplete ? (
        <div
          ref={afterHeroRef}
          className={`page-after-hero${pageReady ? " page-after-hero--ready" : ""}`}
          aria-hidden={pageReady ? undefined : true}
          inert={!pageReady}
        >
          <BuildTypes onBuildAction={onBuildAction} />
          <EasyBuild onBuildAction={onBuildAction} />
          <EasyScale onBuildAction={onBuildAction} />
          <Business />
          <Feedback />
          <FAQ />
          <FinalReveal onGetStarted={onGetStarted} footer={footer} />
        </div>
      ) : null}
    </main>
  );
}
